/**
 * MOCK ADAPTER — in-memory implementation of {@link MailApi}.
 *
 * This file mirrors the backend contract in `./contract.ts` exactly; it exists
 * so the entire UI renders and behaves standalone with `VITE_USE_MOCKS=true`
 * and no Go backend running. Mutations really mutate the in-memory store, so
 * archive/flag/move/delete behave the way the real thing will.
 *
 * DELETE OR IGNORE THIS FILE ONCE THE GO BACKEND IS LIVE — it is fixture
 * plumbing, never production behaviour. All seed data lives in
 * `src/mocks/fixtures.ts`.
 *
 * ## Where it sits
 *
 * The mock implements `MailApi` **above** the transport, so it speaks
 * `types.ts` (camelCase) directly and performs no wire-case conversion: that
 * belongs to `HttpTransport` alone (ADR 0009). What it does mirror exactly is
 * the *shapes* and the *account scoping* — every account-owned method takes
 * `accountId` first and resolves ids inside that account, so a call that
 * forgets its account fails here rather than surviving until someone runs
 * against the real backend.
 */

import { API_BASE } from './http';
import { ApiError, errorEnvelope } from './http';
import { ApiErrorCodes } from './errors';
import {
  CROSS_FOLDER_SORTS,
  DEFAULT_CROSS_FOLDER_SORT,
  DEFAULT_MESSAGE_SORT,
  UNDO_SEND_CHOICES,
} from './types';
import type { ApiErrorCodeLike } from './errors';
import { MAX_BULK_MESSAGES, MAX_TRANSFER_MESSAGES, type MailApi } from './contract';
import { createMockAiApi } from './mockAi';
import { toWire } from './wire';
import { createMockNextcloud } from './mockNextcloud';
import { createMockTasksApi, type MockMailRef, type MockTaskSeed } from './mockTasks';
import { createMockLabels, mockLabelKeyword, type MockLabelSeed } from './mockLabels';
import { createMockSchedulingApi } from './mockScheduling';
import { createMockSentCopies, type MockSentCopies, type MockSentCopyFailure } from './mockSentCopies';
import type {
  Account,
  AddressBook,
  AddressBookInput,
  AddressBookPatch,
  BackgroundAccountStatus,
  BackgroundPushStatus,
  Attendee,
  AttendeeStatus,
  Calendar,
  CalendarEvent,
  CalendarEventInput,
  CalendarInput,
  CalendarPatch,
  CalendarSubscriptionInput,
  Contact,
  ContactGroup,
  ContactImportReport,
  ContactImportResult,
  ContactInput,
  ConversationDetail,
  ConversationSummary,
  Draft,
  DraftInput,
  FlagUpdate,
  Folder,
  ChoosableFolderRole,
  FolderRole,
  FolderRoleChoices,
  FolderRoleSource,
  MessageAuthentication,
  MessageFlag,
  MessageDetail,
  MessageSortOrder,
  MessageSummary,
  TransferItemResult,
  NewMailPoll,
  NotificationPreferences,
  NotificationPreview,
  NotificationRule,
  NotificationRuleInput,
  PageQuery,
  Paginated,
  PushSubscriptionRecord,
  RemoteContentRule,
  RemoteContentRuleInput,
  RemoteMode,
  SearchPage,
  Session,
  SessionSummary,
  SendInput,
  SendResult,
  SieveAction,
  SieveActionType,
  SieveCondition,
  SieveConditionField,
  SieveConditionOp,
  SieveRule,
  SieveBackup,
  SieveBackupCause,
  SieveState,
  SieveTestResult,
  UserPreferences,
  VacationSettings,
  VacationState,
  ConnectionProbe,
  ExternalProvider,
  OAuthIssuer,
  ProvidersResponse,
} from './types';
import type { BulkResult } from './types';
import {
  MOCK_CONTACT_PHOTO_PNG,
  MOCK_PUSH_PUBLIC_KEY,
  buildAttachedMessage,
  buildMessageAuthentication,
  buildMessageDetail,
  buildRawSource,
  createSeedData,
  formatAddress,
  recountFolders,
  type MockCalendarShareLink,
  type MockSeed,
} from '@/mocks/fixtures';
import { toVCardFile } from '@/features/contacts/vcard';
import {
  MOCK_IMPORT_MAX_BYTES,
  MOCK_IMPORT_MAX_CARDS,
  parseMockVcardFile,
} from './mockContactsImport';
import { roomPrefixProblem, videoServerProblem } from '@/features/calendar/videoCallSettings';

/* ----------------------------------------------------------------- options */

export interface MockApiOptions {
  /**
   * Base artificial latency in milliseconds. Every call sleeps for roughly this
   * long (plus a small deterministic jitter) so skeleton states are visible.
   * Pass `0` to make the mock resolve as fast as a microtask — handy in tests.
   */
  latencyMs?: number;
  /**
   * Whether the simulated session starts authenticated. Defaults to `true` so
   * `VITE_USE_MOCKS=true` still drops you straight into the mail UI.
   *
   * Pass `false` to exercise the cold-start path: bootstrap gets a 401, the
   * guard redirects to `/login`, and signing in with any seeded address works.
   */
  signedIn?: boolean;
  /**
   * MOCK: the simulated session is an administrator's (`session.admin`). The
   * mock does not simulate the admin API itself -- it lives outside `MailApi`
   * -- so this only decides whether the Admin entry is drawn.
   */
  admin?: boolean;
  /**
   * MOCK: this many extra, generated *other* sessions on top of the three
   * default rows, to see Settings → Sessions the way an account that never
   * signs out does (the owner's has 527). Default 0. In the dev server set
   * `VITE_MOCK_EXTRA_SESSIONS=500`.
   */
  extraSessions?: number;
  /**
   * MOCK: every send's copy to Sent "fails" and is kept for a retry (#84), so
   * the Mail view's sent-copy banner can be seen. `pending` is filed by the
   * next run a minute later; `failed` waits for Save, download or Dismiss. In
   * the dev server set `VITE_MOCK_SENT_COPY=pending` or `failed`.
   */
  sentCopyFailure?: MockSentCopyFailure;
  /**
   * MOCK: `GET /accounts/providers` answers `enabled: false` (an admin turned
   * external accounts off, ADR 0107), so the add form offers only our own
   * mail server. Default false.
   */
  externalAccountsOff?: boolean;
  /**
   * MOCK: which OAuth issuers are "configured" (ADR 0109). Default
   * `['google']`: Gmail offers Google sign-in first and an app password
   * second, and Outlook stays unavailable. `['google', 'microsoft']` makes
   * Outlook connectable by Microsoft sign-in. `[]` is a server with no OAuth
   * clients, where Gmail is app passwords only as before.
   */
  oauthIssuers?: readonly OAuthIssuer[];
}

/**
 * Out-of-band handle on the mock's simulated session.
 *
 * With the real backend the session lives in `HttpOnly` cookies the mock has
 * no way to fake — so it simulates the only thing the frontend can actually
 * observe: whether `GET /auth/session` answers, and whether everything else
 * 401s. `expireSession()` is how a test or a dev console reproduces "the
 * session died while I was typing" without a backend.
 */
export interface MockSessionControls {
  /** Server-side expiry: every subsequent call 401s until a login. */
  expireSession(): void;
  /** Marks the simulated backend's datastore as down (503 `upstream_failure`). */
  setUpstreamAvailable(available: boolean): void;
  isSignedIn(): boolean;
  /**
   * MOCK: mail arriving in a folder, as the IMAP server would file it. The
   * message joins the thread named by `threadId` (a new one when absent). No
   * event is pushed — there is no stream in mock mode — so the caller drives
   * whatever the stream would have said.
   */
  receiveMail(message: Partial<MessageSummary> & { accountId: string; folderId: string }): MessageSummary;
  /** MOCK: from now on, sends keep their copy to Sent (#84); `undefined` files it again. */
  setSentCopyFailure(failure: MockSentCopyFailure | undefined): void;
}

const DEFAULT_LATENCY_MS = 120;

/**
 * MOCK: the id of the simulated current session.
 *
 * Shaped like the real one — the first 16 hex characters of the token *hash*
 * (docs/plan/14 §5.3) — so a UI that assumed a UUID here would break against
 * the mock exactly as it would against the backend.
 */
const CURRENT_SESSION_ID = '9f2c1a7b3e4d5f60';

/* ------------------------------------------------------------------ errors */

/**
 * Mock errors go over the exact same wire shape as the real backend's
 * `httpapi.ErrorEnvelope`, so a frontend that mis-parses the envelope fails
 * against the mocks too rather than being masked by a friendlier local shape.
 */
let mockRequestSeq = 0;

function mockError(
  status: number,
  code: ApiErrorCodeLike,
  message: string,
  details?: Record<string, unknown>,
): ApiError {
  mockRequestSeq += 1;
  return new ApiError(
    status,
    errorEnvelope(code, message, details, `mock-${mockRequestSeq.toString(36)}`),
  );
}

/**
 * The consent wording version the backend requires (push/background). Version
 * 2 added reading the calendar for reminders; 3 added scheduled send and snooze;
 * 4 added finishing a copy of sent mail to Sent (#84).
 */
const BACKGROUND_PUSH_CONSENT_VERSION = 4;
/**
 * The consent wording version the backend requires for a connected mailbox
 * (push/background/accounts). A separate constant from the primary's: the two
 * explanations are different texts and are bumped independently.
 */
const BACKGROUND_ACCOUNT_CONSENT_VERSION = 4;

function notFound(kind: string, id: string): ApiError {
  return mockError(404, ApiErrorCodes.NotFound, `${kind} "${id}" was not found.`);
}

function invalid(message: string): ApiError {
  return mockError(400, ApiErrorCodes.BadRequest, message);
}

function conflict(message: string): ApiError {
  return mockError(409, ApiErrorCodes.Conflict, message);
}

/**
 * MOCK: the "are you sure?" refusal a non-empty collection answers a
 * `DELETE` without `?confirm=` with.
 *
 * The count travels in the refusal and nowhere else, so the dialog that
 * renders it cannot show a number the server did not just measure. `changed`
 * marks the second refusal — the caller echoed a count back and it had moved.
 */
function confirmDeleteRefusal(
  kind: 'calendar' | 'address_book',
  name: string,
  itemCount: number,
  changed = false,
): ApiError {
  const noun = kind === 'calendar' ? 'event' : 'contact';
  // `conflict`, matching the handler: the refusal is identified by
  // `details.reason`, which is what every reader here branches on, and the mock
  // mirrors the server rather than picking the tidier-sounding code.
  return mockError(
    409,
    ApiErrorCodes.Conflict,
    `"${name}" still holds ${itemCount} ${itemCount === 1 ? noun : `${noun}s`}.`,
    {
      reason: 'confirm_delete',
      kind,
      name,
      item_count: itemCount,
      ...(changed ? { changed: true } : {}),
    },
  );
}

/** MOCK: a `422` whose `details.reason` names which rule the feed URL broke. */
function unprocessable(reason: 'blocked' | 'unreachable' | 'not_calendar', message: string): ApiError {
  return mockError(422, ApiErrorCodes.BadRequest, message, { reason });
}

/**
 * MOCK: the SSRF guard and the fetch, as far as fixtures can simulate them.
 *
 * Deliberately refuses by *shape* rather than by a list of magic strings the
 * UI could learn: anything that is not http/https is blocked, so is anything
 * pointed at a loopback or private address, and a URL that does not look like
 * a calendar document is `not_calendar`. The real guard resolves DNS; this one
 * cannot, and does not pretend to.
 */
function checkFeedUrl(raw: string): void {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw unprocessable('blocked', 'That is not a URL. Feed addresses start with https://.');
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw unprocessable(
      'blocked',
      `${url.protocol.replace(':', '')} addresses can't be subscribed to. Use an http or https URL.`,
    );
  }
  const host = url.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host.endsWith('.local') ||
    /^(127\.|10\.|192\.168\.|169\.254\.|0\.)/.test(host) ||
    host === '[::1]'
  ) {
    throw unprocessable('blocked', `${url.hostname} is on a private network, so it can't be fetched.`);
  }
  if (host.includes('unreachable') || url.pathname.includes('404')) {
    throw unprocessable('unreachable', `${url.hostname} did not answer. Check the address and try again.`);
  }
  if (!/\.ics($|\?)/.test(`${url.pathname}${url.search}`) && !url.pathname.includes('calendar')) {
    throw unprocessable('not_calendar', 'That address answered with something that is not a calendar.');
  }
}

/** MOCK: the backend's fixed palette, cycled so a new collection is never colourless. */
/**
 * The per-calendar share-link cap and label limit the backend enforces.
 *
 * A cap exists at all because each link is a live bearer credential: an
 * unbounded list is an unbounded number of ways in, and a list nobody can read
 * is a list nobody audits. 20 is the server's number, restated here so the
 * mock refuses at the same point the real thing does.
 */
const MOCK_MAX_SHARE_LINKS = 20;
const MOCK_MAX_SHARE_LINK_LABEL = 80;
/** The server's expiry cap: a year, plus a day's slack for the client clock. */
const MOCK_MAX_SHARE_LINK_LIFETIME_MS = 366 * 24 * 60 * 60 * 1000;

const MOCK_CALENDAR_PALETTE = [
  '#3b7dd8',
  '#c3423f',
  '#2e8b57',
  '#b8860b',
  '#7b5ea7',
  '#00838f',
  '#d2691e',
  '#546e7a',
] as const;

function invalidCredentials(message: string): ApiError {
  return mockError(401, ApiErrorCodes.InvalidCredentials, message);
}

/** A wrong step-up password, shaped as the server's (sessions.go writeStepUpRefused). */
function stepUpRefused(email: string): ApiError {
  return mockError(
    401,
    ApiErrorCodes.InvalidCredentials,
    `That isn't the password for ${email}, the account you signed in with.`,
    { attempts_remaining: 4, password_for: 'signed_in_account', account_email: email },
  );
}

/** A connected mailbox's own password refused (accounts_connected.go). */
function mailboxPasswordRefused(email: string): ApiError {
  return mockError(
    401,
    ApiErrorCodes.InvalidCredentials,
    `The mail host did not accept this password for ${email}.`,
    { attempts_remaining: 4, password_for: 'mailbox', account_email: email },
  );
}

function sessionExpired(): ApiError {
  return mockError(401, ApiErrorCodes.SessionExpired, 'Your session has expired. Sign in again.');
}

function upstreamDown(): ApiError {
  return mockError(
    503,
    ApiErrorCodes.UpstreamFailure,
    'The mail service is temporarily unavailable.',
    { retry_after_sec: 5 },
  );
}

/**
 * Wraps every method of one resource group in the simulated session check.
 *
 * Doing it here rather than at 60 call sites keeps the mock honest — a new
 * endpoint is guarded the moment it is added — and mirrors the real backend,
 * where the cookie check is middleware, not per-handler code.
 */
function guardGroup<T extends object>(
  group: T,
  gate: () => ApiError | null,
  exempt: ReadonlySet<string> = new Set<string>(),
): T {
  const guarded: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(group)) {
    if (typeof value !== 'function') {
      guarded[name] = value;
      continue;
    }
    const fn = value as (...args: unknown[]) => unknown;
    guarded[name] = (...args: unknown[]): unknown => {
      if (!exempt.has(name)) {
        const refusal = gate();
        if (refusal !== null) return Promise.reject(refusal);
      }
      return fn(...args);
    };
  }
  // Safe: every key keeps its own signature, only the body gained a prefix.
  return guarded as T;
}

function abortError(): Error {
  if (typeof DOMException === 'function') {
    return new DOMException('The operation was aborted.', 'AbortError');
  }
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
}

/* ----------------------------------------------------------------- helpers */

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(abortError());
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function encodeCursor(offset: number): string {
  return `off:${offset}`;
}

function decodeCursor(cursor?: string): number {
  if (cursor === undefined || cursor === '') return 0;
  const raw = cursor.startsWith('off:') ? cursor.slice(4) : cursor;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 0) throw invalid(`Malformed cursor "${cursor}".`);
  return parsed;
}

function paginate<T>(items: readonly T[], query: PageQuery): Paginated<T> {
  const offset = decodeCursor(query.cursor);
  const limit = Math.min(Math.max(query.limit ?? 50, 1), 500);
  const page = items.slice(offset, offset + limit);
  const next = offset + page.length;
  return {
    items: page,
    total: items.length,
    ...(next < items.length ? { nextCursor: encodeCursor(next) } : {}),
  };
}

/**
 * Pulls `in:` terms out of a search line. MOCK: the real parser is Go's
 * (backend/internal/mail/search.go); this is only enough to route the mock.
 */
function splitInTerms(query: string): { text: string; inTerms: string[] } {
  const inTerms: string[] = [];
  const rest: string[] = [];
  for (const token of query.split(/\s+/)) {
    const match = /^in:(.+)$/i.exec(token);
    if (match) inTerms.push(match[1]!.replace(/^"|"$/g, ''));
    else if (token !== '') rest.push(token);
  }
  return { text: rest.join(' '), inTerms };
}

/** `in:sent`, `in:spam`, `in:Receipts` — a role first, then a name or path. */
function resolveInFolder(folders: readonly Folder[], term: string): Folder | undefined {
  const wanted = term.toLowerCase();
  const role = wanted === 'spam' ? 'junk' : wanted;
  return (
    folders.find((f) => f.role === role && role !== 'custom') ??
    folders.find((f) => f.name.toLowerCase() === wanted || f.path.toLowerCase() === wanted)
  );
}

function matchesText(message: MessageSummary, needle: string): boolean {
  const q = needle.trim().toLowerCase();
  if (q === '') return true;
  const from = `${message.from.name ?? ''} ${message.from.email}`.toLowerCase();
  return (
    message.subject.toLowerCase().includes(q) ||
    from.includes(q) ||
    message.snippet.toLowerCase().includes(q)
  );
}

function sortMessages(items: MessageSummary[], sort: MessageSortOrder | undefined): MessageSummary[] {
  const by: Record<string, (a: MessageSummary, b: MessageSummary) => number> = {
    // MOCK: the store keeps one date per message, so arrival and sent are the
    // same value here. On a real server they are INTERNALDATE and the `Date:`
    // header, and a spoofed header is exactly what makes them differ.
    arrival: (a, b) => Date.parse(a.date) - Date.parse(b.date),
    date: (a, b) => Date.parse(a.date) - Date.parse(b.date),
    from: (a, b) => (a.from.name ?? a.from.email).localeCompare(b.from.name ?? b.from.email),
    subject: (a, b) => a.subject.localeCompare(b.subject),
    size: (a, b) => a.sizeBytes - b.sizeBytes,
  };
  const [key, direction] = (sort ?? DEFAULT_MESSAGE_SORT).split('-');
  const compare = by[key ?? ''] ?? by.arrival;
  const sign = direction === 'asc' ? 1 : -1;
  // Tiebroken on id so the mock's pages are stable, which is what the real
  // SORT's secondary ARRIVAL criterion buys on a server.
  return [...items].sort((a, b) => sign * (compare?.(a, b) ?? 0) || a.id.localeCompare(b.id));
}

/**
 * MOCK: every order, because the mock store is not an IMAP server and has no
 * SORT extension to lack. A real account can come back with only the two
 * arrival orders, which is the case the sort control must still handle.
 */
const MOCK_SORTS_AVAILABLE: MessageSortOrder[] = [
  'arrival-desc',
  'arrival-asc',
  'date-desc',
  'date-asc',
  'from-asc',
  'from-desc',
  'subject-asc',
  'subject-desc',
  'size-desc',
  'size-asc',
];

/**
 * MOCK: the opaque folder id for a new folder — `base64url(account:path)`,
 * matching the fixtures' encoding (ADR 0012). Nothing in the app may decode
 * it; it exists so a created folder's id is shaped like a real one.
 */
function mockFolderId(accountId: string, path: string): string {
  const bytes = new TextEncoder().encode(`${accountId}:${path}`);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function icsTimestamp(value: string, allDay: boolean): string {
  const date = new Date(value);
  const pad2 = (n: number): string => String(n).padStart(2, '0');
  const stamp =
    `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}`;
  if (allDay) return stamp;
  return `${stamp}T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`;
}

function eventToIcs(event: CalendarEvent): string[] {
  return [
    'BEGIN:VEVENT',
    `UID:${event.uid}`,
    `SUMMARY:${event.title}`,
    `DTSTART:${icsTimestamp(event.start, event.allDay)}`,
    `DTEND:${icsTimestamp(event.end, event.allDay)}`,
    `STATUS:${event.status.toUpperCase()}`,
    ...(event.location !== undefined ? [`LOCATION:${event.location}`] : []),
    ...(event.description !== undefined ? [`DESCRIPTION:${event.description}`] : []),
    ...(event.recurrenceRule !== undefined ? [`RRULE:${event.recurrenceRule}`] : []),
    ...(event.organizer !== undefined ? [`ORGANIZER;CN=${event.organizer.name ?? ''}:mailto:${event.organizer.email}`] : []),
    ...event.attendees.map(
      (a) => `ATTENDEE;CN=${a.name ?? ''};PARTSTAT=${a.status.toUpperCase()}:mailto:${a.email}`,
    ),
    'END:VEVENT',
  ];
}

/* --------------------------------------------------------------- the store */

/**
 * MOCK: the mutable store. Built from a fresh seed graph, so nothing here
 * aliases the fixture module's own objects.
 */
/** MOCK: a remote-content rule plus the account it belongs to. */
type MockRemoteRule = RemoteContentRule & { accountId: string };

interface MockStore extends MockSeed {
  nextId: number;
  /**
   * ADR 0018 `remote_content_rules`. The wire shape carries no `account_id`
   * (the path does), so the store keeps one alongside rather than inferring
   * ownership from call order.
   */
  remoteContentRules: MockRemoteRule[];
  /** ADR 0021 `notification_rules`, per account. */
  notificationRules: NotificationRule[];
  /** Settings → General; `undefined` until the first save, like the table. */
  userPreferences: UserPreferences | undefined;
  /** POST/DELETE /push/background. The mock stores no password at all. */
  backgroundPush: BackgroundPushStatus;
  /**
   * POST/DELETE /push/background/accounts/{id}, for connected mailboxes only.
   * Also no password; a mailbox with no entry here is off.
   */
  backgroundAccounts: BackgroundAccountStatus[];
  /** PUT /notifications/snooze: the owner-scope snooze (#143), if any. */
  ownerSnoozedUntil: string | undefined;
  /** `PHOTO` bytes per contact id, served by `contacts.photo()`. */
  contactPhotos: Record<string, { type: string; base64: string }>;
  /**
   * vCard UIDs per contact id, for the import's duplicate check. Only cards
   * the mock imported have one: the seeded cards and the editor's never
   * carried a UID on the wire, and the server mints theirs out of sight.
   */
  contactUids: Record<string, string>;
  /** Copies of each mailbox's Sieve script kept before every write, newest first. */
  sieveBackups: Record<string, SieveBackup[]>;
  /**
   * #68: the folder chosen for each role, per account, as folder ids. The
   * real table stores the raw mailbox name; an id is the same thing here
   * (it is derived from the path), and a rename makes it stale exactly as
   * a rename in another app makes the real one.
   */
  folderRoleChoices: Record<string, Partial<Record<ChoosableFolderRole, string>>>;
  /** #68: each folder's role before any choice, so Automatic can be put back. */
  automaticRoles: Record<string, { role: FolderRole; roleSource: FolderRoleSource | undefined }>;
}

/**
 * The keys `wireContactInput` (backend/internal/httpapi/contacts.go) names, in
 * camelCase. The backend decodes with `DisallowUnknownFields`, so any other key
 * is a 400 on the whole request; the mock refuses it the same way, or a client
 * bug that sends `isGroup` or `id` would pass every mock-mode test and fail on
 * the real server (#343). `contactInput.contract.test.ts` pins this list to
 * the Go struct.
 */
export const CONTACT_INPUT_KEYS: readonly string[] = [
  'addressBookId',
  'displayName',
  'firstName',
  'lastName',
  'organization',
  'title',
  'emails',
  'phones',
  'addresses',
  'nickname',
  'urls',
  'birthday',
  'anniversary',
  'notes',
  'categories',
  'photoBase64',
  'photoRemove',
];

function refuseUnknownContactKeys(input: object): void {
  const unknown = Object.keys(input).filter((key) => !CONTACT_INPUT_KEYS.includes(key));
  if (unknown.length > 0) {
    throw mockError(400, ApiErrorCodes.BadRequest, `json: unknown field "${unknown[0]}"`);
  }
}

/**
 * What `GET /preferences` answers before anything was saved. The same values
 * as `store.DefaultUserPrefs`: each describes what the app does with no
 * preference at all.
 */
const DEFAULT_USER_PREFERENCES: UserPreferences = {
  locale: '',
  timeZone: '',
  markReadEnabled: true,
  markReadDelaySeconds: 0,
  showSnippets: true,
  conversationView: true,
  undoSendSeconds: 5,
  signature: '',
  signatureOnReply: false,
  videoCallUrl: '',
  videoCallRoomPrefix: '',
};

/**
 * MOCK: the server's video-call write rule. `sent` absent keeps the event's
 * call, `''` removes it, a URL sets it; a new call fills an empty location,
 * and removing one clears a location that was only its URL. (The real server
 * also writes the CONFERENCE property and a description block, then strips
 * the block again on read, so neither is visible here.)
 */
function withMockVideoCall<T extends { location?: string }>(
  fields: T,
  had: string | undefined,
  sent: string | undefined,
): T & { videoCallUrl?: string } {
  const next = sent === undefined ? had : sent.trim();
  if (next !== undefined && next !== '') {
    if (!next.startsWith('https://')) throw invalid('video_call_url must be an https:// address');
    const location = fields.location === undefined || fields.location === '' ? next : fields.location;
    return { ...fields, location, videoCallUrl: next };
  }
  if (had !== undefined && fields.location === had) {
    const { location: _gone, ...rest } = fields;
    return rest as T;
  }
  return fields;
}

/**
 * The mock's connection test (#521): deterministic, so a screenshot or an
 * e2e run always shows the same rows. The password `wrong` is refused by
 * IMAP, which leaves SMTP and DAV untried as the backend does; a domain
 * ending in `.test` has no Poste admin path, so Filters is unavailable.
 */
/**
 * MOCK: the compiled-in provider catalogue (ADR 0107, `internal/providers`),
 * as `GET /accounts/providers` serves it with Outlook not configured.
 */
const MOCK_PROVIDERS: readonly ExternalProvider[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    auth: 'app_password',
    authMethods: ['app_password'],
    available: true,
    helpUrl: 'https://myaccount.google.com/apppasswords',
    imapHost: 'imap.gmail.com',
    smtpHost: 'smtp.gmail.com',
    domains: ['gmail.com', 'googlemail.com'],
  },
  {
    id: 'yahoo',
    name: 'Yahoo',
    auth: 'app_password',
    authMethods: ['app_password'],
    available: true,
    helpUrl: 'https://login.yahoo.com/account/security',
    imapHost: 'imap.mail.yahoo.com',
    smtpHost: 'smtp.mail.yahoo.com',
    domains: ['yahoo.com', 'ymail.com', 'rocketmail.com', 'yahoo.co.uk'],
  },
  {
    id: 'icloud',
    name: 'iCloud',
    auth: 'app_password',
    authMethods: ['app_password'],
    available: true,
    helpUrl: 'https://account.apple.com',
    imapHost: 'imap.mail.me.com',
    smtpHost: 'smtp.mail.me.com',
    domains: ['icloud.com', 'me.com', 'mac.com'],
  },
  {
    id: 'aol',
    name: 'AOL',
    auth: 'app_password',
    authMethods: ['app_password'],
    available: true,
    helpUrl: 'https://login.aol.com/account/security',
    imapHost: 'imap.aol.com',
    smtpHost: 'smtp.aol.com',
    domains: ['aol.com', 'aim.com'],
  },
  {
    id: 'outlook',
    name: 'Outlook',
    auth: 'oauth2',
    authMethods: [],
    available: false,
    unavailableReason: 'Outlook.com needs a Microsoft sign-in, which is not set up on this server.',
    helpUrl: 'https://account.microsoft.com/security',
    imapHost: 'outlook.office365.com',
    smtpHost: 'smtp-mail.outlook.com',
    domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'],
  },
];

/** MOCK: the issuer each OAuth-capable catalogue entry uses (ADR 0109). */
const MOCK_OAUTH_ISSUER: Readonly<Record<string, OAuthIssuer>> = { gmail: 'google', outlook: 'microsoft' };

/**
 * MOCK: the catalogue as served with `issuers` configured (ADR 0109): an
 * entry whose issuer is configured lists `oauth2` first, and Outlook becomes
 * available when Microsoft is configured. `auth` stays the first usable method.
 */
function mockCatalogue(issuers: readonly OAuthIssuer[]): ExternalProvider[] {
  return MOCK_PROVIDERS.map((entry) => {
    const issuer = MOCK_OAUTH_ISSUER[entry.id];
    const copy: ExternalProvider = { ...entry, domains: [...entry.domains], authMethods: [...(entry.authMethods ?? [])] };
    if (issuer === undefined || !issuers.includes(issuer)) return copy;
    const methods = ['oauth2' as const, ...(copy.authMethods ?? [])];
    const { unavailableReason: _reason, ...rest } = copy;
    return { ...rest, auth: 'oauth2', authMethods: methods, oauthIssuer: issuer, available: true };
  });
}

/**
 * MOCK: the catalogue entry `provider` names, refused as the backend refuses
 * it: unknown or turned off is a 400 on the field, OAuth-only a 501.
 */
function mockProvider(
  provider: string | undefined,
  enabled: boolean,
  catalogue: readonly ExternalProvider[] = MOCK_PROVIDERS,
): ExternalProvider | undefined {
  if (provider === undefined) return undefined;
  const entry = enabled ? catalogue.find((candidate) => candidate.id === provider) : undefined;
  if (entry === undefined) {
    throw mockError(400, ApiErrorCodes.BadRequest, 'That provider is not available on this server.', {
      field: 'provider',
    });
  }
  if (!entry.available) {
    throw mockError(
      501,
      ApiErrorCodes.UnsupportedByHost,
      entry.unavailableReason ?? `${entry.name} is not available on this server.`,
      { feature: 'provider' },
    );
  }
  return entry;
}

/** MOCK: an external mailbox's probe -- mail only; no DAV, no filters (ADR 0107). */
function mockExternalProbe(provider: ExternalProvider, email: string, password: string): ConnectionProbe {
  const base = mockConnectionProbe(email, password);
  return {
    results: base.results.map((row) =>
      row.protocol === 'dav'
        ? { protocol: 'dav', status: 'unavailable', detail: `Calendar and contacts are not available for ${provider.name} mailboxes.` }
        : row.protocol === 'filters'
          ? { protocol: 'filters', status: 'unavailable', detail: `Filters are not available for ${provider.name} mailboxes.` }
          : row,
    ),
  };
}

function mockConnectionProbe(email: string, password: string): ConnectionProbe {
  const filters =
    email.toLowerCase().endsWith('.test')
      ? { protocol: 'filters' as const, status: 'unavailable' as const, detail: 'Filters are not available for this mail host.' }
      : { protocol: 'filters' as const, status: 'ok' as const, detail: 'Available.' };
  if (password === 'wrong') {
    const notTried = 'Not tried: the mail host refused this password for incoming mail.';
    return {
      results: [
        { protocol: 'imap', status: 'failed', detail: 'The mail host refused this password.' },
        { protocol: 'smtp', status: 'skipped', detail: notTried },
        { protocol: 'dav', status: 'skipped', detail: notTried },
        filters,
      ],
    };
  }
  return {
    results: [
      { protocol: 'imap', status: 'ok', detail: 'Signed in.' },
      { protocol: 'smtp', status: 'ok', detail: 'Signed in.' },
      { protocol: 'dav', status: 'ok', detail: 'Signed in.' },
      filters,
    ],
  };
}

function requireAccount(store: MockStore, accountId: string): Account {
  const account = store.accounts.find((a) => a.id === accountId);
  if (account === undefined) throw notFound('Account', accountId);
  return account;
}

/**
 * Resolves a folder **within one account**.
 *
 * `404`, never `403`, when the id belongs to somebody else's account: that is
 * the backend's rule (doc 10 §1.8) and confirming existence would leak it.
 */
function requireFolder(store: MockStore, accountId: string, folderId: string): Folder {
  const folder = store.folders.find((f) => f.id === folderId && f.accountId === accountId);
  if (folder === undefined) throw notFound('Folder', folderId);
  return folder;
}

/** MOCK: the hierarchy delimiter this account's server reports, INBOX first. */
function mockDelimiter(store: MockStore, accountId: string): string {
  const folders = store.folders.filter((f) => f.accountId === accountId);
  return (
    folders.find((f) => f.role === 'inbox' && f.delimiter !== '')?.delimiter ??
    folders.find((f) => f.delimiter !== '')?.delimiter ??
    ''
  );
}

/** MOCK: every folder below this one. */
function mockDescendants(store: MockStore, accountId: string, folder: Folder): Folder[] {
  if (folder.delimiter === '') return [];
  const prefix = `${folder.path}${folder.delimiter}`;
  return store.folders.filter((f) => f.accountId === accountId && f.path.startsWith(prefix));
}

/**
 * MOCK: the refusal that keeps Inbox, Sent, Drafts, Junk and Trash where the
 * app can find them — and covers a folder that merely HOLDS one, because
 * renaming the parent moves the role folder just as surely.
 */
function mockRoleGuard(store: MockStore, accountId: string, folder: Folder, verb: string): void {
  const blocked =
    folder.role !== 'custom'
      ? folder
      : mockDescendants(store, accountId, folder).find((f) => f.role !== 'custom');
  if (blocked === undefined) return;
  throw mockError(
    409,
    ApiErrorCodes.Conflict,
    blocked.id === folder.id
      ? `This is one of the folders the app uses by name, so it cannot be ${verb}.`
      : `This folder holds ${blocked.name}, which the app uses by name, so it cannot be ${verb}.`,
    { reason: 'role_folder', role: blocked.role, folder_id: blocked.id },
  );
}

/** The five roles a person may choose, in the order the wire lists them (#68). */
const CHOOSABLE_ROLES: readonly ChoosableFolderRole[] = ['archive', 'sent', 'drafts', 'trash', 'junk'];

/**
 * MOCK of the backend's resolution with choices (#68, ADR 0105): every
 * folder starts from its automatic role, then each choice whose folder still
 * exists takes its role from whoever held it. A choice beats the server's own
 * marking, and the chosen folder holds no other role.
 */
function applyMockFolderRoles(store: MockStore, accountId: string): void {
  const folders = store.folders.filter((f) => f.accountId === accountId);
  for (const folder of folders) {
    if (store.automaticRoles[folder.id] === undefined) {
      store.automaticRoles[folder.id] = { role: folder.role, roleSource: folder.roleSource };
    }
    const auto = store.automaticRoles[folder.id]!;
    folder.role = auto.role;
    if (auto.roleSource === undefined) delete folder.roleSource;
    else folder.roleSource = auto.roleSource;
  }
  const choices = store.folderRoleChoices[accountId] ?? {};
  for (const role of CHOOSABLE_ROLES) {
    const chosen = folders.find((f) => f.id === choices[role]);
    if (chosen === undefined) continue;
    for (const other of folders) {
      if (other !== chosen && other.role === role) {
        other.role = 'custom';
        delete other.roleSource;
      }
    }
    chosen.role = role;
    chosen.roleSource = 'user';
  }
}

function mockFolderRoleChoices(store: MockStore, accountId: string): FolderRoleChoices {
  const folders = store.folders.filter((f) => f.accountId === accountId);
  const choices = store.folderRoleChoices[accountId] ?? {};
  return {
    roles: CHOOSABLE_ROLES.map((role) => {
      const folderId = choices[role] ?? null;
      const automatic = folders.find((f) => (store.automaticRoles[f.id]?.role ?? f.role) === role);
      return {
        role,
        folderId,
        stale: folderId !== null && !folders.some((f) => f.id === folderId),
        automaticFolderId: automatic?.id ?? null,
      };
    }),
  };
}

/** MOCK: a fixture's Message-ID, as `buildMessageDetail` writes it (without the angle brackets). */
function mockRfcMessageId(message: MessageSummary): string {
  return `${message.id}@dao-mail.com`;
}

/** MOCK: where a message is, for the task link (ADR 0075). */
function mockMailRef(
  store: MockStore,
  accountId: string,
  match: (message: MessageSummary) => boolean,
): MockMailRef | undefined {
  const message = store.messages.find((m) => m.accountId === accountId && match(m));
  if (message === undefined) return undefined;
  const folder = store.folders.find((f) => f.id === message.folderId && f.accountId === accountId);
  return {
    messageId: message.id,
    folderId: message.folderId,
    folderRole: folder?.role ?? 'custom',
    rfcMessageId: mockRfcMessageId(message),
  };
}

function requireMessage(store: MockStore, accountId: string, messageId: string): MessageSummary {
  const message = store.messages.find((m) => m.id === messageId && m.accountId === accountId);
  if (message === undefined) throw notFound('Message', messageId);
  return message;
}

/** Bulk mutations answer 200 with per-item outcomes, even when all of them failed. */
/** MOCK: which source message each transferred copy came from (ADR 0108). */
const transferOrigins = new WeakMap<MessageSummary, string>();

function bulkApply(
  ids: readonly string[],
  apply: (id: string) => string | undefined,
): BulkResult {
  const result: BulkResult = { applied: [], failed: [] };
  if (ids.length === 0) throw invalid('At least one message id is required.');
  // The server's cap, enforced here too so a client that forgets to page
  // fails in tests the way it would against the real handler.
  if (ids.length > MAX_BULK_MESSAGES) {
    throw mockError(400, ApiErrorCodes.BadRequest, 'too many messages in one request', {
      field: 'message_ids',
      max: MAX_BULK_MESSAGES,
    });
  }
  for (const messageId of ids) {
    try {
      const newId = apply(messageId);
      result.applied.push(newId === undefined ? { id: messageId } : { id: messageId, newId });
    } catch (cause) {
      result.failed.push({
        id: messageId,
        code: cause instanceof ApiError ? cause.code : ApiErrorCodes.Internal,
        message: cause instanceof Error ? cause.message : 'The operation failed.',
      });
    }
  }
  return result;
}

function roleFolder(store: MockStore, accountId: string, role: Folder['role']): Folder {
  const folder = store.folders.find((f) => f.accountId === accountId && f.role === role);
  if (folder === undefined) throw notFound(`Folder with role "${role}" on account`, accountId);
  return folder;
}

/** MOCK: the id prefix the server gives a blocked-sender rule. */
const BLOCKED_RULE_PREFIX = 'blocked-';

/**
 * MOCK: the Sieve state a block is written into, with the handler's refusals:
 * `501` for a host without managed filtering, `409 foreign_script` for a
 * script the builder cannot own, `409 diverged_script` for one edited
 * outside it.
 */
function blockableSieve(store: MockStore, accountId: string): SieveState {
  const account = requireAccount(store, accountId);
  if (!account.capabilities.sieve) {
    throw mockError(
      501,
      ApiErrorCodes.UnsupportedByHost,
      'This mailbox is on a host without managed filtering.',
      { feature: 'sieve' },
    );
  }
  const state =
    store.sieve[accountId] ?? ({ script: null, source: 'managed', rules: [] } as SieveState);
  if (state.source === 'foreign') {
    throw mockError(
      409,
      ApiErrorCodes.Conflict,
      'This mailbox has a filter script OLS cannot edit, so senders can’t be blocked here.',
      { reason: 'foreign_script' },
    );
  }
  if (state.source === 'diverged') {
    throw mockError(
      409,
      ApiErrorCodes.Conflict,
      'The filter script was changed outside OLS. Open Settings → Filters to resolve it first.',
      { reason: 'diverged_script' },
    );
  }
  return state;
}

/** MOCK: replace the managed rules and recompile, as a builder save does. */
function writeRules(
  store: MockStore,
  accountId: string,
  state: SieveState,
  rules: SieveRule[],
): void {
  store.sieve[accountId] = {
    ...state,
    rules,
    script: {
      name: state.script?.name ?? 'ols-managed',
      active: true,
      script: compileRules(rules),
      updatedAt: new Date().toISOString(),
    },
  };
}

/** Deep-enough copy so a caller cannot mutate the store through the result. */
/**
 * What saving the listed rules would write, for "View differences" (#138).
 * The backend sends it on every state it returns, for a diverged script only.
 */
function withProposed(state: SieveState): SieveState {
  return state.source === 'diverged' && state.rules !== null
    ? { ...state, proposedScript: compileRules(state.rules) }
    : state;
}

function cloneSieve(state: SieveState): SieveState {
  return {
    ...state,
    script: state.script === null ? null : { ...state.script },
    rules: state.rules === null ? null : state.rules.map((rule) => ({ ...rule })),
    ...(state.vacation === undefined
      ? {}
      : { vacation: state.vacation === null ? null : { ...state.vacation } }),
  };
}

/* ------------------------------------------------------ sieve backups */

/** MOCK: what the backend's retention keeps (`internal/sieve` backups). */
const MOCK_SIEVE_RETENTION = { keepLatest: 20, keepDays: 30, maxKept: 100 } as const;

/**
 * MOCK: a stand-in fingerprint (FNV-1a, hex). The real one is sha256; the
 * mock state carries no `scriptHash` to compare it with anyway, so all it has
 * to do is be the same for the same text.
 */
function mockScriptHash(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function mockBackup(
  id: number,
  script: string,
  createdAt: string,
  cause: SieveBackupCause,
  detail: string,
  actor: string,
  device: string,
): SieveBackup {
  return {
    id,
    createdAt,
    cause,
    detail,
    actor,
    device,
    bytes: new TextEncoder().encode(script).length,
    sha256: mockScriptHash(script),
    script,
  };
}

/**
 * MOCK: a copy of the script as it is NOW, kept before a write replaces it —
 * every Sieve write on the backend does this first. Newest first, pruned to
 * `maxKept`.
 */
function keepSieveBackup(
  store: MockStore,
  accountId: string,
  cause: SieveBackupCause,
  detail = '',
): void {
  const script = store.sieve[accountId]?.script?.script ?? '';
  const list = store.sieveBackups[accountId] ?? [];
  const id = (list[0]?.id ?? 0) + 1;
  store.sieveBackups[accountId] = [
    mockBackup(
      id,
      script,
      new Date().toISOString(),
      cause,
      detail,
      store.session.account.email,
      'This browser (mock)',
    ),
    ...list,
  ].slice(0, MOCK_SIEVE_RETENTION.maxKept);
}

/**
 * MOCK: two earlier versions of the primary mailbox's script, so Settings →
 * Filters → Previous versions has something to show, diff and restore.
 */
function seedSieveBackups(store: MockStore): Record<string, SieveBackup[]> {
  const primary = store.accounts.find((account) => account.isPrimary);
  const current = primary === undefined ? '' : (store.sieve[primary.id]?.script?.script ?? '');
  if (primary === undefined || current === '') return {};
  const withoutNewsletters = current.replace(
    /\n\nif header :contains "list-id" "terminal-weekly" \{[\s\S]*?\n\}/,
    '',
  );
  const older = withoutNewsletters.replace(
    'if header :contains "subject" ["invoice", "receipt"]',
    'if header :contains "subject" "invoice"',
  );
  const now = Date.now();
  const hour = 60 * 60 * 1000;
  return {
    [primary.id]: [
      mockBackup(
        2,
        withoutNewsletters,
        new Date(now - 26 * hour).toISOString(),
        'filters',
        '',
        primary.email,
        'Firefox on Linux',
      ),
      mockBackup(
        1,
        older,
        new Date(now - 6 * 24 * hour).toISOString(),
        'block_sender',
        'offers@shop.example',
        primary.email,
        '',
      ),
    ],
  };
}

/**
 * MOCK: the checks the backend makes before it writes a vacation block
 * (internal/sieve/vacation.go). Close enough that the settings page's error
 * copy can be exercised without a server; the server remains the authority.
 */
function vacationProblem(v: VacationSettings): string | null {
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if ([...v.subject].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)) {
    return 'The subject must be a single line without control characters.';
  }
  if (v.body.length > 16 * 1024) return 'The message must be at most 16 KiB.';
  if (!Number.isInteger(v.days) || v.days < 1 || v.days > 30) {
    return 'Replies can repeat every 1 to 30 days.';
  }
  if (v.startDate !== '' && !day.test(v.startDate)) return 'The first day is not a date.';
  if (v.endDate !== '' && !day.test(v.endDate)) return 'The last day is not a date.';
  if (!v.enabled) return null;
  if (v.startDate === '') return 'Choose the first day of your out-of-office.';
  if (v.endDate !== '' && v.endDate < v.startDate) {
    return 'The last day cannot be before the first day.';
  }
  if (v.body.trim() === '') return 'Write the message people will receive.';
  return null;
}

/** MOCK: the vacation card as the backend would describe it. */
function vacationStateOf(store: MockStore, account: Account): VacationState {
  const sieve = store.sieve[account.id];
  const vacation = sieve?.vacation ?? null;
  const conflict = (sieve?.rules ?? []).find(
    (rule) => rule.enabled && rule.actions.some((action) => action.type === 'vacation'),
  );
  return {
    vacation: vacation === null ? null : { ...vacation },
    state: vacation === null ? 'none' : 'managed',
    from: account.email,
    addresses: [account.email, ...account.aliases],
    ...(conflict === undefined ? {} : { conflict: `the filter rule “${conflict.name}”` }),
  };
}

/** MOCK: a plausible Sieve rendering of the structured rules. */
function compileRules(rules: readonly SieveRule[]): string {
  return [
    'require ["fileinto", "imap4flags"];',
    '',
    ...rules
      .filter((rule) => rule.enabled)
      .flatMap((rule) => [
        `# ${rule.name}`,
        `if ${rule.match === 'all' ? 'allof' : 'anyof'} (${rule.conditions
          .map((c) => `header :contains "${c.headerName ?? c.field}" "${c.value}"`)
          .join(', ')}) {`,
        // plan/17 §7: a label is `addflag` of its keyword, and `setflag`
        // compiles to `addflag` too, so a later flag action cannot strip it.
        ...rule.actions.map((a) => {
          const verb = a.type === 'label' || a.type === 'setflag' ? 'addflag' : a.type;
          return a.value === undefined ? `  ${verb};` : `  ${verb} "${a.value}";`;
        }),
        ...(rule.stopProcessing ? ['  stop;'] : []),
        '}',
        '',
      ]),
  ].join('\n');
}

/**
 * MOCK: reads the few script shapes the mock itself writes and a hand-written
 * script typically uses — `if [allof|anyof] (tests) { actions }` with
 * header/address tests and size :over/:under. Anything else answers null.
 */
function parseMockScript(script: string): SieveRule[] | null {
  const text = script.replace(/#.*$/gm, '').replace(/require\s+[^;]*;/g, '').trim();
  const rules: SieveRule[] = [];
  const ruleRe = /^if\s+(?:(allof|anyof)\s*\(([^)]*)\)|([^{]*))\s*\{([^}]*)\}\s*/;
  let rest = text;
  while (rest !== '') {
    const m = ruleRe.exec(rest);
    if (m === null) return null;
    rest = rest.slice(m[0].length).trim();
    const tests = (m[2] ?? m[3] ?? '').split(/,(?=\s*(?:header|address|size|not)\b)/);
    const conditions: SieveCondition[] = [];
    for (const test of tests) {
      const t = test.trim();
      const size = /^size\s+:(over|under)\s+(\d+)([KMG]?)$/i.exec(t);
      if (size) {
        const unit = { '': 1, K: 1024, M: 1024 ** 2, G: 1024 ** 3 }[(size[3] ?? '').toUpperCase()] ?? 1;
        conditions.push({
          field: 'size',
          op: size[1] === 'over' ? 'greater-than' : 'less-than',
          value: String(Number(size[2]) * unit),
        });
        continue;
      }
      const hdr = /^(not\s+)?(?:header|address)\s+:(contains|is|matches)\s+"([^"]*)"\s+"([^"]*)"$/i.exec(t);
      if (!hdr) return null;
      const name = (hdr[3] ?? '').toLowerCase();
      const known = ['from', 'to', 'cc', 'subject'].includes(name);
      const op = hdr[1] ? 'not-contains' : (hdr[2] as SieveConditionOp);
      conditions.push({
        field: known ? (name as SieveConditionField) : 'header',
        ...(known ? {} : { headerName: hdr[3] ?? '' }),
        op,
        value: hdr[4] ?? '',
      });
    }
    const actions: SieveAction[] = [];
    for (const statement of (m[4] ?? '').split(';')) {
      const a = /^\s*(\w+)(?:\s+"([^"]*)")?\s*$/.exec(statement);
      if (statement.trim() === '') continue;
      if (!a) return null;
      // `addflag` of a label keyword reads back as a label; any other flag
      // action reads back as `setflag`, as it always did.
      const verb = a[1] === 'addflag' || a[1] === 'setflag'
        ? a[2] !== undefined && /^\$label[_=]/i.test(a[2])
          ? 'label'
          : 'setflag'
        : a[1];
      actions.push({ type: verb as SieveActionType, ...(a[2] !== undefined ? { value: a[2] } : {}) });
    }
    rules.push({
      id: `imported-${rules.length + 1}`,
      name: `Rule ${rules.length + 1}`,
      enabled: true,
      match: m[1] === 'anyof' ? 'any' : 'all',
      conditions,
      actions,
      stopProcessing: false,
      order: rules.length,
    });
  }
  return rules;
}

/** Contacts are addressed without their book, so the account is the only scope. */
/**
 * MOCK: the session owner's row with `status` set, added if they were not on
 * the list — the same forgiving rule `respond` uses, and the same one the real
 * server needs for an invite that arrived at an alias.
 */
function withSelfStatus(
  attendees: readonly Attendee[],
  selfEmail: string,
  status: AttendeeStatus,
): Attendee[] {
  const me = selfEmail.trim().toLowerCase();
  let found = false;
  const next = attendees.map((attendee): Attendee => {
    if (attendee.email.trim().toLowerCase() !== me) return { ...attendee };
    found = true;
    return { ...attendee, status };
  });
  return found ? next : [...next, { email: selfEmail, status }];
}

/** Resolves an address book **within one account** — 404, never 403. */
function requireBook(store: MockStore, accountId: string, bookId: string): AddressBook {
  const book = store.addressBooks.find((b) => b.id === bookId && b.accountId === accountId);
  if (book === undefined) throw notFound('Address book', bookId);
  return book;
}

/** MOCK: the next palette entry this account has not used, so two new rows differ. */
function nextPaletteColor(store: MockStore, accountId: string): string {
  const used = new Set(
    store.calendars.filter((c) => c.accountId === accountId).map((c) => c.color.toLowerCase()),
  );
  return (
    MOCK_CALENDAR_PALETTE.find((color) => !used.has(color)) ?? MOCK_CALENDAR_PALETTE[0]
  );
}

function requireCalendar(store: MockStore, accountId: string, calendarId: string): Calendar {
  const calendar = store.calendars.find((c) => c.id === calendarId && c.accountId === accountId);
  if (calendar === undefined) throw notFound('Calendar', calendarId);
  return calendar;
}

/** Event ids are `base64url(href)` and do not repeat their collection. */
function requireEvent(store: MockStore, accountId: string, eventId: string): CalendarEvent {
  const calendarIds = new Set(
    store.calendars.filter((c) => c.accountId === accountId).map((c) => c.id),
  );
  const event = store.events.find((e) => e.id === eventId && calendarIds.has(e.calendarId));
  if (event === undefined) throw notFound('Event', eventId);
  return event;
}

function requireContact(store: MockStore, accountId: string, contactId: string): Contact {
  const bookIds = new Set(
    store.addressBooks.filter((b) => b.accountId === accountId).map((b) => b.id),
  );
  const contact = store.contacts.find(
    (c) => c.id === contactId && bookIds.has(c.addressBookId),
  );
  if (contact === undefined) throw notFound('Contact', contactId);
  return contact;
}

/**
 * MOCK: every draft id a save, discard or send has retired, with the identity
 * (ADR 0100) its draft carried -- what lets a save over a retired version be
 * refused as `draft_changed` instead of answering a bare 404.
 */
const retiredDrafts = new WeakMap<MockStore, Map<string, string>>();

function retireDraft(store: MockStore, draft: Draft): void {
  if (draft.draftUuid === undefined) return;
  let retired = retiredDrafts.get(store);
  if (retired === undefined) {
    retired = new Map();
    retiredDrafts.set(store, retired);
  }
  retired.set(draft.id, draft.draftUuid);
}

/**
 * MOCK: the server's `LocateDraftBase` for a base that is not there any more:
 * 409 `changed` naming the newest version of the same draft, 409 `gone` when
 * the draft is known and has none, and the plain 404 for an id never seen.
 */
function draftBaseRefusal(store: MockStore, accountId: string, draftId: string, input: DraftInput): ApiError {
  const uuid = retiredDrafts.get(store)?.get(draftId) ?? input.draftUuid;
  if (uuid === undefined) return notFound('Draft', draftId);
  const current = store.drafts.find((d) => d.accountId === accountId && d.draftUuid === uuid);
  if (current !== undefined) {
    return mockError(409, ApiErrorCodes.DraftChanged, 'this draft was changed in another window or on another device', {
      reason: 'changed',
      current_id: current.id,
      current_updated_at: current.updatedAt,
    });
  }
  return mockError(409, ApiErrorCodes.DraftChanged, 'this draft was sent or deleted in another window or on another device', {
    reason: 'gone',
  });
}

function requireDraft(store: MockStore, accountId: string, draftId: string): Draft {
  const draft = store.drafts.find((d) => d.id === draftId && d.accountId === accountId);
  if (draft === undefined) throw notFound('Draft', draftId);
  return draft;
}

/**
 * MOCK: files a composed message into Sent and reports a real {@link SendResult}.
 *
 * Shared by `compose.send` (from a stored draft) and `compose.sendDirect` (the
 * LT path), because the outcome the UI has to render is identical.
 */
function deliver(
  store: MockStore,
  copies: MockSentCopies,
  accountId: string,
  draft: DraftInput,
  messageId: string,
): SendResult {
  if (draft.to.length === 0) throw invalid('At least one recipient is required.');
  const sent = roleFolder(store, accountId, 'sent');
  const account = store.accounts.find((a) => a.id === accountId);
  const summary: MessageSummary = {
    id: messageId,
    accountId,
    folderId: sent.id,
    threadId: draft.inReplyToMessageId ?? `thr-${messageId}`,
    subject: draft.subject,
    from: { name: account?.displayName ?? '', email: draft.fromAddress },
    to: draft.to.map((a) => ({ ...a })),
    date: new Date().toISOString(),
    snippet: draft.bodyText ?? draft.bodyHtml.replace(/<[^>]+>/g, ' ').trim().slice(0, 140),
    flags: ['seen'],
    hasAttachments: draft.attachmentIds.length > 0,
    sizeBytes: 2_048 + draft.bodyHtml.length,
  };
  const recipients = [...draft.to, ...draft.cc, ...draft.bcc].map((a) => a.email);
  const sentResult = {
    messageId: `<${messageId}@dao-mail.com>`,
    queuedAt: new Date().toISOString(),
    accepted: recipients,
    rejected: [],
  };
  // As the server does (#84): no copy when the mailbox files sent mail itself,
  // and a kept copy with the server's own sentence when the APPEND "failed".
  if (!copies.saves(accountId)) {
    return { ...sentResult, savedToSent: false, sentFiledByServer: true };
  }
  const kept = copies.keep(summary, recipients);
  if (kept !== undefined) {
    return {
      ...sentResult,
      savedToSent: false,
      sentCopy: kept,
      warning:
        kept.state === 'failed'
          ? 'Sent. A copy couldn’t be saved to your Sent folder.'
          : kept.unattended
            ? 'Sent. A copy couldn’t be saved to your Sent folder yet; it will be tried again.'
            : 'Sent. A copy couldn’t be saved to your Sent folder yet; it will be tried again while you’re signed in.',
    };
  }
  const filed = fileIntoSent(store, summary);
  return {
    ...sentResult,
    savedToFolderId: sent.id,
    savedToSent: true,
    sentUid: filed.uid,
    sentMessageId: messageId,
  };
}

/** MOCK: a UID for each message filed into Sent, as UIDPLUS would report it. */
let mockSentUid = 900;

/** MOCK: puts a sent message into its mailbox's Sent folder. */
function fileIntoSent(store: MockStore, message: MessageSummary): { folderId: string; uid: number } {
  const sent = roleFolder(store, message.accountId, 'sent');
  store.messages.push({ ...message, folderId: sent.id });
  recountFolders(store.folders, store.messages);
  mockSentUid += 1;
  return { folderId: sent.id, uid: mockSentUid };
}

/** MOCK: every mutation funnels through here so counts stay believable. */
function resync(store: MockStore): void {
  recountFolders(store.folders, store.messages);
}

/* ------------------------------------------ MOCK: conversations (ADR 0077) */

/** Oldest first, as the server orders members (by UID; here by date then id). */
function byDateAsc(a: MessageSummary, b: MessageSummary): number {
  return Date.parse(a.date) - Date.parse(b.date) || a.id.localeCompare(b.id);
}

interface MockConversation {
  conversation: ConversationSummary;
  members: MessageSummary[];
  sent: MessageSummary[];
}

/**
 * MOCK: a folder's conversations, built the way plan/16 §3–§4 describes, with
 * the fixtures' `threadId` standing in for `THREAD=REFERENCES`: one
 * conversation per thread id per folder, and Sent messages of the same thread
 * merged in (they count and add a Sent label; they never move the row).
 */
function mockConversations(
  store: MockStore,
  accountId: string,
  folderId: string,
  filters: { unreadOnly?: boolean; flaggedOnly?: boolean } = {},
): MockConversation[] {
  const folder = requireFolder(store, accountId, folderId);
  const account = store.accounts.find((a) => a.id === accountId);
  const own = (account?.email ?? '').toLowerCase();
  const groups = new Map<string, MessageSummary[]>();
  for (const message of store.messages) {
    if (message.accountId !== accountId || message.folderId !== folderId) continue;
    const group = groups.get(message.threadId);
    if (group) group.push(message);
    else groups.set(message.threadId, [message]);
  }
  const sentFolder = store.folders.find((f) => f.accountId === accountId && f.role === 'sent');
  const mergesSent =
    sentFolder !== undefined && !['sent', 'drafts', 'trash', 'junk'].includes(folder.role);
  const sentByThread = new Map<string, MessageSummary[]>();
  if (mergesSent) {
    for (const message of store.messages) {
      if (message.accountId !== accountId || message.folderId !== sentFolder.id) continue;
      const group = sentByThread.get(message.threadId);
      if (group) group.push(message);
      else sentByThread.set(message.threadId, [message]);
    }
  }

  const out: MockConversation[] = [];
  for (const [threadId, group] of groups) {
    const members = [...group].sort(byDateAsc);
    if (filters.unreadOnly && members.every((m) => m.flags.includes('seen'))) continue;
    if (filters.flaggedOnly && !members.some((m) => m.flags.includes('flagged'))) continue;
    const sent = [...(sentByThread.get(threadId) ?? [])].sort(byDateAsc);
    const root = members[0]!;
    const head = members[members.length - 1]!;
    const everyone = [...members, ...sent].sort(byDateAsc);
    const participants: ConversationSummary['participants'] = [];
    for (const message of everyone) {
      const email = message.from.email.toLowerCase();
      const unread = message.folderId === folderId && !message.flags.includes('seen');
      const known = participants.find((p) => p.email.toLowerCase() === email);
      if (known) {
        if (unread) known.unread = true;
        continue;
      }
      if (participants.length >= 8) continue;
      participants.push({
        ...(message.from.name ? { name: message.from.name } : {}),
        email: message.from.email,
        isMe: email === own || message.folderId !== folderId,
        unread,
      });
    }
    const flags: MessageFlag[] = [];
    if (members.every((m) => m.flags.includes('seen'))) flags.push('seen');
    if (members.some((m) => m.flags.includes('flagged'))) flags.push('flagged');
    if (members.some((m) => m.flags.includes('answered'))) flags.push('answered');
    out.push({
      members,
      sent,
      conversation: {
        // MOCK: opaque to the client; minted from the root, as the server's is.
        id: `conv-${folderId}-${root.id}`,
        accountId,
        folderId,
        subject: root.subject,
        snippet: head.snippet,
        date: head.date,
        newestMessageId: head.id,
        messageIds: members.map((m) => m.id),
        messageCount: members.length + sent.length,
        unreadCount: members.filter((m) => !m.flags.includes('seen')).length,
        flags,
        hasAttachments: everyone.some((m) => m.hasAttachments),
        participants,
        labels:
          sent.length > 0 && sentFolder
            ? [{ folderId: sentFolder.id, name: sentFolder.name, role: 'sent' }]
            : [],
        labelKeywords: unionKeywords(everyone),
        sentMessageIds: sent.map((m) => m.id),
      },
    });
  }
  out.sort(
    (a, b) =>
      Date.parse(b.conversation.date) - Date.parse(a.conversation.date) ||
      b.conversation.newestMessageId.localeCompare(a.conversation.newestMessageId),
  );
  return out;
}

/** Every label keyword of these messages, de-duplicated case-insensitively. */
function unionKeywords(messages: readonly MessageSummary[]): string[] {
  const out: string[] = [];
  for (const message of messages) {
    for (const keyword of message.labelKeywords ?? []) {
      if (!out.some((held) => held.toLowerCase() === keyword.toLowerCase())) out.push(keyword);
    }
  }
  return out;
}

/** MOCK: the detail the reading pane asks for, for any message id. */
function mockConversationDetail(
  store: MockStore,
  accountId: string,
  messageId: string,
): ConversationDetail {
  const message = requireMessage(store, accountId, messageId);
  const found = mockConversations(store, accountId, message.folderId).find((entry) =>
    entry.conversation.messageIds.includes(messageId),
  );
  if (found === undefined) throw notFound('Message', messageId);
  const messages = [...found.members, ...found.sent].sort(byDateAsc).map((m) => ({
    ...m,
    flags: [...m.flags],
    threadId: found.conversation.id,
    inFolder: m.folderId === message.folderId,
  }));
  const kept = messages.slice(-100);
  return {
    conversation: found.conversation,
    messages: kept,
    omittedCount: messages.length - kept.length,
  };
}

/** MOCK: a message's body as plain text, as the server's `ReadableText` gives the assistant. */
function mockReadableText(detail: MessageDetail): string {
  if (detail.bodyText !== undefined && detail.bodyText !== '') return detail.bodyText;
  return (detail.bodyHtml ?? '')
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    // A link keeps its address, as the server's ReadableText does.
    .replace(/<a\b[^>]*\bhref="(https?:[^"]+)"[^>]*>([\s\S]*?)<\/a>/gi, (_m, href: string, label: string) =>
      label.replace(/<[^>]+>/g, '').trim() === href ? href : `${label} (${href})`,
    )
    .replace(/<blockquote[^>]*>/gi, '\n> ')
    .replace(/<(br|\/p|\/div|\/li|\/h\d)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');
}

/* --------------------------------------------- MOCK: remote content (0018) */

/**
 * MOCK: which fixture messages pretend to carry remote images.
 *
 * Deterministic from the id so a given message always renders the same bar,
 * and roughly a third of them do, which is about right for real mail.
 */
function mockRemoteShape(messageId: string): { assets: number; trackers: number } {
  let hash = 0;
  for (const char of messageId) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  if (hash % 3 === 0) return { assets: 0, trackers: 0 };
  return { assets: (hash % 4) + 1, trackers: hash % 2 };
}

/**
 * MOCK: the sanitizer's two modes, as far as the frontend can tell them apart.
 *
 * In `block` mode every asset is a sized placeholder pointing at our own
 * `/remote-blocked.png`; in `load` mode each becomes a sealed-ref URL on our
 * origin. **Trackers appear in neither**: they are removed by the server and
 * are never fetchable, which is the property the reading-pane test pins.
 */
function withMockRemoteContent(
  detail: MessageDetail,
  mode: RemoteMode,
  appliedRule: MockRemoteRule | undefined,
): MessageDetail {
  const shape = mockRemoteShape(detail.id);
  if (shape.assets === 0 && shape.trackers === 0) return detail;

  const loaded = mode === 'load';
  const images: string[] = [];
  for (let i = 0; i < shape.assets; i += 1) {
    images.push(
      loaded
        ? `<img src="/api/v1/accounts/${detail.accountId}/messages/${detail.id}/remote/mockref${String(i).padStart(40, '0')}" width="480" height="180" alt="Newsletter banner">`
        : '<img class="ols-remote-placeholder" src="/remote-blocked.png" width="480" height="180" alt="Newsletter banner">',
    );
  }

  return {
    ...detail,
    bodyHtml: `${detail.bodyHtml ?? ''}\n<div class="mock-remote">${images.join('\n')}</div>`,
    sanitizeReport: {
      ...(detail.sanitizeReport ?? { scriptsRemoved: 0, formsRemoved: 0, remoteImagesBlocked: 0 }),
      remoteImagesBlocked: loaded ? 0 : shape.assets + shape.trackers,
    },
    remoteContent: {
      mode: loaded ? 'loaded' : 'blocked',
      blocked: loaded ? 0 : shape.assets,
      loaded: loaded ? shape.assets : 0,
      trackersRemoved: shape.trackers,
      ...(appliedRule
        ? {
            appliedRule: {
              kind: appliedRule.kind,
              value: appliedRule.value,
              // MOCK: fixtures have no Authentication-Results, so a persisted
              // rule is treated as verified rather than inventing a failure.
              verified: true,
            },
          }
        : {}),
    },
  };
}

/** MOCK: the server's rule match — `all`, then the exact address, then domains. */
function matchRemoteRule(
  store: MockStore,
  accountId: string,
  address: string,
): MockRemoteRule | undefined {
  const rules = store.remoteContentRules.filter((rule) => rule.accountId === accountId);
  const email = address.trim().toLowerCase();
  const domain = email.slice(email.indexOf('@') + 1);
  return (
    rules.find((rule) => rule.kind === 'all') ??
    rules.find((rule) => rule.kind === 'sender' && rule.value === email) ??
    rules
      .filter(
        (rule) =>
          rule.kind === 'domain' &&
          (rule.value === domain ||
            (rule.value.startsWith('.') && domain.endsWith(rule.value.slice(1)))),
      )
      .sort((a, b) => b.value.length - a.value.length)[0]
  );
}

/* ---------------------------------------- MOCK: notification rules (0021) */

/**
 * MOCK: the server's precedence table (ADR 0021 §4.3), first match wins.
 *
 * It lives here because the **mock is the server** when `VITE_USE_MOCKS=true`.
 * No part of the app calls it: the client never evaluates rules, and the only
 * way this decision reaches the UI is as a `decision`/`reason` pair on a
 * response, exactly as the Go evaluator's would.
 */
/** The owner-wide snooze while it runs, shaped for spreading into a record. */
function runningOwnerSnooze(store: MockStore): { ownerSnoozedUntil?: string } {
  const until = store.ownerSnoozedUntil;
  return until !== undefined && Date.parse(until) > Date.now() ? { ownerSnoozedUntil: until } : {};
}

function mockEvaluate(
  store: MockStore,
  accountId: string,
  message: MessageSummary,
): { decision: 'notify' | 'no'; reason: string; matchedRule?: NotificationRule } {
  const prefs = store.notificationPreferences.find((p) => p.accountId === accountId);
  if (prefs !== undefined && !prefs.enabled) {
    return { decision: 'no', reason: 'Notifications are switched off for this account.' };
  }

  const email = message.from.email.trim().toLowerCase();
  const domain = email.slice(email.indexOf('@') + 1);
  const rules = store.notificationRules.filter((rule) => rule.accountId === accountId);

  const sender = rules.find((rule) => rule.kind === 'sender' && rule.value === email);
  if (sender) {
    return {
      decision: sender.action === 'notify' ? 'notify' : 'no',
      reason: `A sender rule ${sender.action === 'notify' ? 'always notifies' : 'mutes'} ${sender.value}.`,
      matchedRule: sender,
    };
  }

  const [domainRule] = rules
    .filter(
      (rule) =>
        rule.kind === 'domain' &&
        (rule.value === domain ||
          (rule.value.startsWith('.') && domain.endsWith(rule.value.slice(1)))),
    )
    .sort((a, b) => b.value.length - a.value.length);
  if (domainRule) {
    return {
      decision: domainRule.action === 'notify' ? 'notify' : 'no',
      reason: `A domain rule ${domainRule.action === 'notify' ? 'always notifies' : 'mutes'} ${domainRule.value}.`,
      matchedRule: domainRule,
    };
  }

  const watched =
    prefs !== undefined && prefs.folderIds.length > 0
      ? prefs.folderIds
      : [store.folders.find((f) => f.accountId === accountId && f.role === 'inbox')?.id ?? ''];
  if (watched.includes(message.folderId)) {
    return { decision: 'notify', reason: 'This folder is on the watch list.' };
  }
  return { decision: 'no', reason: 'This folder is not on the watch list.' };
}

/* ------------------------------------------------------------------- entry */

/**
 * Builds a fresh mock API over a private in-memory store, plus the handle that
 * drives its simulated session.
 *
 * `createMockApi()` (zero args) is the call site in `src/api/index.ts`; the
 * options object exists so tests and Storybook can turn the fake latency off
 * and start signed out.
 */
export function createMockApiWithSession(options: MockApiOptions = {}): {
  api: MailApi;
  session: MockSessionControls;
} {
  const baseLatency = Math.max(options.latencyMs ?? DEFAULT_LATENCY_MS, 0);
  const store: MockStore = {
    ...createSeedData(),
    nextId: 1,
    remoteContentRules: [],
    notificationRules: [],
    userPreferences: undefined,
    backgroundPush: { status: 'off', reason: '', consentVersion: BACKGROUND_PUSH_CONSENT_VERSION },
    backgroundAccounts: [],
    ownerSnoozedUntil: undefined,
    contactPhotos: {},
    contactUids: {},
    sieveBackups: {},
    folderRoleChoices: {},
    automaticRoles: {},
  };
  store.sieveBackups = seedSieveBackups(store);
  for (const contact of store.contacts) {
    if (contact.photoUrl !== undefined) {
      store.contactPhotos[contact.id] = { type: 'image/png', base64: MOCK_CONTACT_PHOTO_PNG };
    }
  }
  const photoUrlFor = (accountId: string, contactId: string): string =>
    `${API_BASE}/accounts/${accountId}/contacts/${contactId}/photo`;

  let tick = 0;
  /** Deterministic-ish jitter: same sequence every run, still feels alive. */
  const oauthIssuers: readonly OAuthIssuer[] = options.oauthIssuers ?? ['google'];
  const catalogue = (): ExternalProvider[] => mockCatalogue(oauthIssuers);
  const delay = (signal?: AbortSignal): Promise<void> => {
    if (baseLatency === 0) return Promise.resolve();
    tick += 1;
    return sleep(baseLatency + ((tick * 37) % 8) * 35, signal);
  };

  const newId = (prefix: string): string => {
    store.nextId += 1;
    return `${prefix}-${String(store.nextId).padStart(5, '0')}`;
  };

  const sessionEmail = (): string => store.session.account.email;

  /*
   * MOCK: copies of sent mail not in Sent yet (#84). Unattended, as on the
   * server, when the mailbox has a background credential: the enrolment
   * Settings → Notifications makes.
   */
  const sentCopies = createMockSentCopies({
    delay,
    newId,
    mockError,
    accountExists: (accountId) => store.accounts.some((a) => a.id === accountId),
    unattended: (accountId) => {
      const account = store.accounts.find((a) => a.id === accountId);
      if (account === undefined) return false;
      return account.isPrimary
        ? store.backgroundPush.status === 'on'
        : store.backgroundAccounts.some(
            (entry) => entry.accountId === accountId && entry.status === 'on',
          );
    },
    file: (message) => fileIntoSent(store, message),
    ...(options.sentCopyFailure !== undefined ? { failure: options.sentCopyFailure } : {}),
  });

  /*
   * MOCK: labels (plan/17). Seeded on the primary mailbox, with keywords on
   * a spread of its Inbox so chips, the label view and `label:` all show
   * something; the connected mailboxes start with none.
   */
  for (const message of store.messages) message.labelKeywords ??= [];
  seedLabelKeywords(store.messages);
  const labels = createMockLabels({
    delay,
    newId,
    accountExists: (accountId) => store.accounts.some((a) => a.id === accountId),
    messages: () => store.messages,
    rewriteFilters: (accountId, map) => {
      const current = store.sieve[accountId];
      const account = store.accounts.find((a) => a.id === accountId);
      if (account?.capabilities.sieve === false) return undefined;
      if (!current?.rules) return 0;
      let changed = 0;
      const rules = current.rules.flatMap((rule) => {
        const actions = map(rule.actions);
        if (JSON.stringify(actions) === JSON.stringify(rule.actions)) return [rule];
        changed += 1;
        // A rule left with nothing to do is removed, as the server does.
        return actions.length === 0 ? [] : [{ ...rule, actions }];
      });
      if (changed > 0) {
        store.sieve[accountId] = {
          ...current,
          rules,
          ...(current.script ? { script: { ...current.script, script: compileRules(rules) } } : {}),
        };
      }
      return changed;
    },
    seed: MOCK_LABEL_SEED,
  });

  /*
   * MOCK: Nextcloud (plan 18). Not linked; in the dev server the Login Flow
   * grants itself on the third poll and a chunk's progress visibly moves, so
   * a person can click all the way through. Tests grant by hand.
   */
  const nextcloud = createMockNextcloud({
    // Save to Nextcloud (ADR 0086): the "server" reads the part from the
    // mailbox itself; the client only names it.
    findPart: (accountId, messageId, partId) => {
      const summary = store.messages.find((m) => m.accountId === accountId && m.id === messageId);
      if (summary === undefined) return undefined;
      const part = buildMessageDetail(summary).attachments.find((a) => a.id === partId);
      return part === undefined
        ? undefined
        : { filename: part.filename, sizeBytes: part.sizeBytes, contentType: part.contentType };
    },
    delay,
    ...(baseLatency === 0 ? {} : { autoGrantAfterPolls: 2 }),
    progressDelayMs: baseLatency === 0 ? 0 : 150,
  });

  /**
   * MOCK: the conversation `state` tokens (plan/16 §5). The real token encodes
   * the folder's UIDNEXT/EXISTS/HIGHESTMODSEQ; this one names a snapshot of
   * what the folder's conversations looked like when it was handed out, which
   * is what `sync` diffs against.
   */
  const conversationStates = new Map<string, { folder: string; rows: Map<string, string> }>();
  let conversationStateSeq = 0;
  const rememberConversationState = (
    accountId: string,
    folderId: string,
    rows: readonly ConversationSummary[],
  ): string => {
    conversationStateSeq += 1;
    const token = `mock-state-${conversationStateSeq}`;
    conversationStates.set(token, {
      folder: `${accountId}/${folderId}`,
      rows: new Map(rows.map((row) => [row.id, JSON.stringify(row)])),
    });
    // Bounded: a mock left running all day must not keep every snapshot.
    if (conversationStates.size > 200) {
      const oldest = conversationStates.keys().next().value;
      if (oldest !== undefined) conversationStates.delete(oldest);
    }
    return token;
  };

  /**
   * MOCK: the whole simulated session. There is no token to fake — with cookie
   * sessions the frontend never sees one — so the simulation is exactly the
   * two things it *can* observe: does `/auth/session` answer, and does
   * everything else 401.
   */
  let signedIn = options.signedIn ?? true;
  let upstreamAvailable = true;
  /** MOCK: whether the simulated session was created with a device key. */
  let bound = true;
  let reboundAt: string | undefined;
  let ticketSeq = 0;

  const currentSession = (): Session => {
    store.session = {
      ...store.session,
      sessionId: CURRENT_SESSION_ID,
      bound,
      serverTime: new Date().toISOString(),
      admin: options.admin === true,
      elevatedUntil: null,
    };
    return { ...store.session, account: { ...store.session.account } };
  };

  /**
   * MOCK session list. Coarse on purpose, exactly like the real response: a
   * masked network and a `<browser> on <OS>` label, because neither the mock
   * nor the backend ever holds a raw User-Agent (ADR 0045).
   */
  let mockSessions: SessionSummary[] = [
    {
      id: CURRENT_SESSION_ID,
      current: true,
      bound: true,
      remembered: true,
      client: 'Firefox on macOS',
      app: false,
      network: '192.168.1.x',
      sameNetwork: true,
      createdAt: new Date(Date.now() - 3 * 86_400_000).toISOString(),
      lastSeenAt: new Date(Date.now() - 60_000).toISOString(),
      reboundAt: null,
      expiresAt: new Date(Date.now() + 11 * 86_400_000).toISOString(),
      absoluteExpiresAt: new Date(Date.now() + 27 * 86_400_000).toISOString(),
    },
    {
      id: 'a41d9c0e77b52f13',
      current: false,
      bound: true,
      remembered: false,
      client: 'Safari on iOS',
      app: true,
      network: '203.0.113.x',
      sameNetwork: false,
      createdAt: new Date(Date.now() - 5 * 3_600_000).toISOString(),
      lastSeenAt: new Date(Date.now() - 22 * 60_000).toISOString(),
      reboundAt: null,
      expiresAt: new Date(Date.now() + 7 * 3_600_000).toISOString(),
      absoluteExpiresAt: new Date(Date.now() + 19 * 3_600_000).toISOString(),
    },
    {
      id: 'c07b2e4498aa61d5',
      current: false,
      bound: false,
      remembered: false,
      client: 'Chrome on Windows',
      app: false,
      network: '198.51.100.x',
      sameNetwork: false,
      createdAt: new Date(Date.now() - 40 * 60_000).toISOString(),
      lastSeenAt: new Date(Date.now() - 4 * 60_000).toISOString(),
      reboundAt: null,
      expiresAt: new Date(Date.now() + 20 * 60_000).toISOString(),
      absoluteExpiresAt: new Date(Date.now() + 80 * 60_000).toISOString(),
    },
  ];
  const extraSessions = Math.max(Math.floor(options.extraSessions ?? 0), 0);
  const extraClients = ['Firefox on Linux', 'Chrome on Android', 'Safari on macOS', 'Edge on Windows'];
  for (let i = 0; i < extraSessions; i += 1) {
    // Deliberately not in activity order, so the page's own sort is exercised.
    const minutesAgo = 10 + ((i * 7919) % (extraSessions * 13 + 1));
    mockSessions.push({
      id: `e${String(i).padStart(15, '0')}`,
      current: false,
      bound: i % 3 !== 0,
      remembered: i % 2 === 0,
      client: extraClients[i % extraClients.length] ?? 'Unknown browser',
      app: i % 5 === 0,
      network: `198.51.${i % 256}.x`,
      sameNetwork: false,
      createdAt: new Date(Date.now() - (minutesAgo + 60) * 60_000).toISOString(),
      lastSeenAt: new Date(Date.now() - minutesAgo * 60_000).toISOString(),
      reboundAt: null,
      expiresAt: new Date(Date.now() + 86_400_000).toISOString(),
      absoluteExpiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
    });
  }

  /** The middleware gate: 503 outranks 401, as it does in the real backend. */
  const gate = (): ApiError | null => {
    if (!upstreamAvailable) return upstreamDown();
    if (!signedIn) return sessionExpired();
    return null;
  };

  const raw: MailApi = {
    /* ---------------------------------------------------------------- auth */
    auth: {
      async login(input, signal) {
        await delay(signal);
        if (input.email.trim() === '' || input.password === '') {
          throw invalid('Email and password are both required.');
        }
        const account = store.accounts.find(
          (a) => a.email.toLowerCase() === input.email.trim().toLowerCase(),
        );
        if (account === undefined) {
          throw invalidCredentials('No mailbox matches that address on this host.');
        }
        // MOCK, but the rule is the real one (ADR 0042): a login with no
        // device key creates an *unbound* session, and an unbound session is
        // short and cannot be remembered however the box was ticked.
        bound = input.deviceKey !== undefined;
        // Remembered: the backend's 30 days idle / 90 days absolute (ADR 0050).
        const remembered = bound && input.remember === true;
        const ttl = remembered ? 30 * 86_400_000 : 43_200_000;
        const absolute = remembered ? 90 * 86_400_000 : ttl * 2;
        const session: Session = {
          expiresAt: new Date(Date.now() + ttl).toISOString(),
          absoluteExpiresAt: new Date(Date.now() + absolute).toISOString(),
          // No user id and no display name: the owner is the address
          // (ADR 0010), and `account.id` is the primary mailbox's id.
          account: {
            id: account.id,
            email: account.email.toLowerCase(),
            domain: account.email.split('@')[1]?.toLowerCase() ?? '',
            authHost: account.host,
          },
          sessionId: CURRENT_SESSION_ID,
          bound,
          serverTime: new Date().toISOString(),
          admin: options.admin === true,
          elevatedUntil: null,
        };
        store.session = session;
        signedIn = true;
        return session;
      },
      async logout() {
        await delay();
        signedIn = false;
      },
      async session(signal) {
        await delay(signal);
        if (!upstreamAvailable) throw upstreamDown();
        if (!signedIn) throw sessionExpired();
        return currentSession();
      },
      async refresh() {
        await delay();
        if (!signedIn) throw sessionExpired();
        store.session = {
          ...store.session,
          expiresAt: new Date(Date.now() + 43_200_000).toISOString(),
        };
        return currentSession();
      },
      async changePassword(accountId, input) {
        await delay();
        const account = requireAccount(store, accountId);
        // The same order and words as password.go: primary only, Poste
        // capability, then the current password, then the new one's rules.
        if (!account.isPrimary) {
          throw mockError(
            409,
            ApiErrorCodes.Conflict,
            "Only your primary mailbox's password can be changed here.",
            { reason: 'not_primary' },
          );
        }
        if (!account.capabilities.passwordChange) {
          throw mockError(
            501,
            ApiErrorCodes.UnsupportedByHost,
            'Password changes are not configured for this mail host.',
          );
        }
        if (input.currentPassword === '') {
          throw invalidCredentials('your mailbox password is required');
        }
        if (input.currentPassword === 'wrong') throw stepUpRefused(store.session.account.email);
        const next = input.newPassword;
        if (next === '') throw invalid('Enter a new password.');
        if (next.trim() === '') throw invalid('The new password cannot be only spaces.');
        if (next === input.currentPassword) {
          throw invalid('The new password must be different from the current one.');
        }
        if ([...next].length < 8) throw invalid('The new password must be at least 8 characters.');
        if ([...next].length > 256) throw invalid('The new password must be at most 256 characters.');
        // Every other session ends; this one is rotated and carries on.
        const revokedSessions = mockSessions.filter((row) => row.id !== CURRENT_SESSION_ID).length;
        mockSessions = mockSessions.filter((row) => row.id === CURRENT_SESSION_ID);
        return { ...currentSession(), revokedSessions };
      },

      async rebind(input) {
        await delay();
        if (input.password === '') throw invalidCredentials('Your password is required.');
        if (input.password === 'wrong') throw stepUpRefused(store.session.account.email);
        if (input.deviceKey.crv !== 'P-256') {
          throw mockError(400, 'device_key_invalid', 'That device key is not a P-256 key.', {
            field: 'device_key',
          });
        }
        bound = true;
        reboundAt = new Date().toISOString();
        return currentSession();
      },

      async sessions(signal) {
        await delay(signal);
        return mockSessions.map((row) =>
          row.id === CURRENT_SESSION_ID
            ? { ...row, bound, ...(reboundAt === undefined ? {} : { reboundAt }) }
            : { ...row },
        );
      },

      async revokeSession(sessionId) {
        await delay();
        if (sessionId === CURRENT_SESSION_ID) {
          // The real backend answers this too: ending your own session is what
          // the ordinary logout is for, and doing it from a list row would
          // leave the page claiming to be signed in.
          throw mockError(409, ApiErrorCodes.Conflict, 'That is the session you are using.', {
            reason: 'current',
          });
        }
        const before = mockSessions.length;
        mockSessions = mockSessions.filter((row) => row.id !== sessionId);
        if (mockSessions.length === before) throw notFound('Session', sessionId);
      },

      async revokeOtherSessions(password, keepEndpoint) {
        await delay();
        if (password === '') throw invalidCredentials('Your password is required.');
        if (password === 'wrong') throw stepUpRefused(store.session.account.email);
        const revokedCount = mockSessions.filter((row) => row.id !== CURRENT_SESSION_ID).length;
        mockSessions = mockSessions.filter((row) => row.id === CURRENT_SESSION_ID);
        // The revoked sessions' push subscriptions go with them; the caller's
        // own device's row, named by `keepEndpoint`, stays. MOCK: rows here
        // carry no session, so without `keepEndpoint` there is no telling
        // whose they are and all of them are left alone.
        if (keepEndpoint !== undefined) {
          store.pushSubscriptions = store.pushSubscriptions.filter(
            (s) => s.endpoint === keepEndpoint,
          );
        }
        return { revokedCount };
      },
    },

    /* -------------------------------------------------------------- events */
    events: {
      async ticket(signal) {
        await delay(signal);
        ticketSeq += 1;
        // Single-use in the real backend (`GETDEL`); the mock just never
        // repeats one, so a caller that tried to reuse a ticket would be as
        // wrong here as it is there.
        return {
          ticket: `mock-ticket-${ticketSeq.toString(36).padStart(6, '0')}`,
          expiresAt: new Date(Date.now() + 60_000).toISOString(),
        };
      },
    },

    /* ------------------------------------------------------------ accounts */
    accounts: {
      async list(signal) {
        await delay(signal);
        return store.accounts.map((a) => ({ ...a }));
      },
      async get(accountId, signal) {
        await delay(signal);
        return { ...requireAccount(store, accountId) };
      },
      async providers(signal): Promise<ProvidersResponse> {
        await delay(signal);
        if (options.externalAccountsOff === true) return { enabled: false, providers: [] };
        return { enabled: true, providers: catalogue() };
      },
      async create(input) {
        await delay();
        const email = input.email.trim();
        const provider = mockProvider(input.provider, options.externalAccountsOff !== true, catalogue());
        if (provider && !(provider.authMethods ?? []).includes('app_password')) {
          throw mockError(
            400,
            ApiErrorCodes.BadRequest,
            `${provider.name} mailboxes are connected by signing in, not with a password.`,
            { field: 'provider' },
          );
        }
        const primary = store.accounts.find((a) => a.isPrimary);
        // The same refusals the backend makes, in the same shapes, so the
        // settings pane is developed against real answers.
        if (primary && primary.email.toLowerCase() === email.toLowerCase()) {
          throw mockError(409, ApiErrorCodes.Conflict, 'That is the mailbox you are signed in with.', {
            reason: 'primary',
          });
        }
        if (store.accounts.some((a) => a.email.toLowerCase() === email.toLowerCase())) {
          throw mockError(409, ApiErrorCodes.Conflict, `${email} is already connected.`, {
            reason: 'duplicate',
          });
        }
        if (input.password === '') throw invalid('A password is required.');
        if (input.password === 'wrong') throw mailboxPasswordRefused(email);
        const account: Account = {
          id: newId('acct'),
          email,
          displayName: input.displayName ?? '',
          // Never the caller's to choose: the backend resolves the host from
          // the admin routing table (SSRF control). The mock has one host.
          host: provider?.imapHost ?? 'mail01.cloud-hosting.io',
          isPrimary: false,
          status: 'connected',
          color: input.color ?? '#7c8fa3',
          ...(provider ? { provider: provider.id, providerName: provider.name } : {}),
          auth: 'password',
          // A connected mailbox gets mail, and its quota. Sieve, password
          // change, calendar and contacts are Poste/DAV features of the
          // primary's host and the backend reports them false for a connected
          // mailbox; quota is not, because it comes from the mailbox's own
          // IMAP QUOTA first and falls back to the admin API of the rule THAT
          // mailbox routes to.
          capabilities: {
            sieve: false,
            quota: true,
            passwordChange: false,
            calendar: false,
            contacts: false,
            push: false,
          },
          // The mock's one host reports a quota root with STORAGE and no
          // MESSAGE resource, which is the shape the live host answers with.
          quota: { storageLimit: 5120, storageUsage: 812, countLimit: 0, countUsage: 0 },
          aliases: [],
        };
        store.accounts.push(account);
        return { ...account };
      },
      async update(accountId, input) {
        await delay();
        const account = requireAccount(store, accountId);
        // As the backend (updateAccount): the primary is named by the sign-in.
        if (account.isPrimary) {
          throw mockError(
            409,
            ApiErrorCodes.Conflict,
            'The primary mailbox is named by your sign-in and cannot be edited here.',
            { reason: 'primary' },
          );
        }
        // Only these two are writable (doc 10 §5.2): the address and the host
        // identify the mailbox, so changing them would make it a different one.
        if (input.displayName !== undefined) account.displayName = input.displayName.trim();
        if (input.color !== undefined) account.color = input.color;
        return { ...account };
      },
      async verify(accountId, password) {
        await delay();
        const account = requireAccount(store, accountId);
        if (account.isPrimary) {
          throw mockError(409, ApiErrorCodes.Conflict, 'The primary mailbox is verified when you sign in.');
        }
        if (account.auth === 'oauth2') {
          throw mockError(
            400,
            ApiErrorCodes.UseOAuth,
            `This mailbox signs in with ${account.providerName ?? 'its provider'}. Sign in again to reconnect it.`,
          );
        }
        if (password === undefined) {
          // A password the mail host has already rejected is never retried.
          if (account.status === 'reauth-required') {
            throw mockError(
              409,
              ApiErrorCodes.StoredCredentialInvalid,
              'The saved password for this mailbox no longer works. Enter it again.',
            );
          }
          return { ...account };
        }
        if (password === '') throw invalid('A password is required.');
        if (password === 'wrong') throw mailboxPasswordRefused(account.email);
        account.status = 'connected';
        delete account.statusMessage;
        return { ...account };
      },
      async probe(accountId, password) {
        await delay();
        const account = requireAccount(store, accountId);
        if (account.isPrimary) {
          throw mockError(409, ApiErrorCodes.Conflict, 'The mailbox you signed in with is checked every time you sign in.');
        }
        if (password === undefined && account.status === 'reauth-required') {
          throw mockError(
            409,
            ApiErrorCodes.StoredCredentialInvalid,
            'The saved password for this mailbox no longer works. Enter it again.',
          );
        }
        // The stored password of a connected mock mailbox is the right one.
        const external = MOCK_PROVIDERS.find((entry) => entry.id === account.provider);
        if (external) return mockExternalProbe(external, account.email, password ?? 'stored');
        return mockConnectionProbe(account.email, password ?? 'stored');
      },
      async probeNew(input) {
        await delay();
        const provider = mockProvider(input.provider, options.externalAccountsOff !== true, catalogue());
        if (input.password === '') throw invalid('A password is required.');
        if (provider) return mockExternalProbe(provider, input.email.trim(), input.password);
        return mockConnectionProbe(input.email.trim(), input.password);
      },
      /*
       * MOCK: there is no Google or Microsoft here, and the mock's data lives
       * in memory, so a real round trip (which reloads the page) would lose
       * it. The sign-in "happens" at once and the answer is the server's
       * callback URL itself, same-origin, which the app follows in-app. The
       * signed-in address is fixed: mock.user@gmail.com, or
       * mock.user@outlook.com for Microsoft.
       */
      async oauthStart(input) {
        await delay();
        const provider = mockProvider(input.provider, options.externalAccountsOff !== true, catalogue());
        if (provider === undefined || provider.oauthIssuer === undefined || !(provider.authMethods ?? []).includes('oauth2')) {
          throw mockError(400, ApiErrorCodes.BadRequest, 'That provider does not offer sign-in on this server.', {
            field: 'provider',
          });
        }
        const back = (query: string) => ({ authorizeUrl: `/settings/accounts?${query}` });
        if (input.accountId !== undefined) {
          const account = requireAccount(store, input.accountId);
          if (account.auth !== 'oauth2' || account.provider !== provider.id) {
            throw mockError(400, ApiErrorCodes.BadRequest, 'That mailbox does not sign in with this provider.', {
              field: 'account_id',
            });
          }
          account.status = 'connected';
          delete account.statusMessage;
          return back(`oauth=connected&account=${encodeURIComponent(account.id)}`);
        }
        const email = `mock.user@${provider.oauthIssuer === 'google' ? 'gmail.com' : 'outlook.com'}`;
        if (store.accounts.some((a) => a.email.toLowerCase() === email)) return back('oauth=error&reason=already_connected');
        const account: Account = {
          id: newId('acct'),
          email,
          displayName: input.displayName?.trim() ?? '',
          host: provider.imapHost,
          isPrimary: false,
          status: 'connected',
          color: input.color ?? '#7c8fa3',
          provider: provider.id,
          providerName: provider.name,
          auth: 'oauth2',
          // A mailbox on another provider has mail only (ADR 0107).
          capabilities: { sieve: false, quota: true, passwordChange: false, calendar: false, contacts: false, push: false },
          quota: { storageLimit: 15360, storageUsage: 2048, countLimit: 0, countUsage: 0 },
          aliases: [],
        };
        store.accounts.push(account);
        return back(`oauth=connected&account=${encodeURIComponent(account.id)}`);
      },
      async remove(accountId) {
        await delay();
        const account = requireAccount(store, accountId);
        if (account.isPrimary) throw invalid('The primary mailbox cannot be removed.');
        store.accounts = store.accounts.filter((a) => a.id !== accountId);
        store.folders = store.folders.filter((f) => f.accountId !== accountId);
        store.messages = store.messages.filter((m) => m.accountId !== accountId);
        store.drafts = store.drafts.filter((d) => d.accountId !== accountId);
      },
      async quota(accountId, signal) {
        await delay(signal);
        const account = requireAccount(store, accountId);
        // What the real backend answers on a host with no Poste service
        // credential (ADR 0008). It was a 404 here, which is the wrong shape
        // to develop against: a 404 says "that mailbox has no quota record",
        // and the UI would go on believing figures exist somewhere.
        if (!account.capabilities.quota || account.quota === undefined) {
          throw mockError(
            501,
            ApiErrorCodes.UnsupportedByHost,
            'Mailbox quota is not configured for this mail host.',
            { feature: 'quota' },
          );
        }
        return { ...account.quota };
      },
    },

    /* ------------------------------------------------------------- folders */
    folders: {
      async list(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        // Flat, like the wire (doc 10 §5.3). The client nests it from
        // `parentId`; a mock that pre-nested would hide a broken tree builder.
        return store.folders
          .filter((f) => f.accountId === accountId)
          .map((f) => ({ ...f }));
      },
      async create(accountId, input) {
        await delay();
        requireAccount(store, accountId);
        const parent =
          input.parentId === undefined ? undefined : requireFolder(store, accountId, input.parentId);
        // MOCK: the delimiter comes off the folders, never from a literal —
        // the real server's is '.' on this deployment and '/' on others, and a
        // mock that hardcoded one would hide a client that hardcoded the same.
        const delimiter = parent?.delimiter ?? mockDelimiter(store, accountId);
        const name = input.name.trim();
        if (name === '') throw invalid('A folder needs a name.');
        if (delimiter !== '' && name.includes(delimiter)) {
          throw mockError(
            400,
            ApiErrorCodes.BadRequest,
            `A folder name cannot contain ${delimiter} — that is what this server uses to separate folders.`,
            { field: 'name', delimiter },
          );
        }
        const path = parent === undefined ? name : `${parent.path}${delimiter}${name}`;
        if (store.folders.some((f) => f.accountId === accountId && f.path === path)) {
          throw conflict('There is already a folder with that name here.');
        }
        const folder: Folder = {
          id: mockFolderId(accountId, path),
          accountId,
          path,
          name,
          parentId: parent?.id ?? null,
          role: 'custom',
          delimiter,
          unreadCount: 0,
          totalCount: 0,
          selectable: true,
          subscribed: true,
          uidValidity: Math.floor(Date.now() / 1000),
        };
        store.folders.push(folder);
        return { ...folder };
      },
      async rename(accountId, folderId, name) {
        await delay();
        const folder = requireFolder(store, accountId, folderId);
        mockRoleGuard(store, accountId, folder, 'renamed');
        const delimiter = folder.delimiter;
        const trimmed = name.trim();
        if (trimmed === '') throw invalid('A folder needs a name.');
        if (delimiter !== '' && trimmed.includes(delimiter)) {
          throw mockError(
            400,
            ApiErrorCodes.BadRequest,
            `A folder name cannot contain ${delimiter} — that is what this server uses to separate folders.`,
            { field: 'name', delimiter },
          );
        }
        const segments = delimiter === '' ? [folder.path] : folder.path.split(delimiter);
        segments[segments.length - 1] = trimmed;
        const nextPath = segments.join(delimiter);
        if (nextPath === folder.path) return { ...folder };
        // Keep descendant paths consistent, exactly as an IMAP RENAME would.
        // MOCK, and load-bearing: the id is derived from the mailbox name, so
        // a rename gives the folder AND every folder under it a new id, as the
        // real server does. A mock that kept the old id hid a URL left
        // pointing at a folder that no longer exists.
        const renamedIds = new Map<string, string>();
        const prefix = `${folder.path}${delimiter}`;
        for (const child of store.folders) {
          if (child.accountId !== accountId) continue;
          if (delimiter !== '' && child.path.startsWith(prefix)) {
            child.path = `${nextPath}${delimiter}${child.path.slice(prefix.length)}`;
            renamedIds.set(child.id, mockFolderId(accountId, child.path));
          }
        }
        folder.name = trimmed;
        folder.path = nextPath;
        renamedIds.set(folder.id, mockFolderId(accountId, nextPath));
        for (const f of store.folders) {
          if (f.accountId !== accountId) continue;
          if (f.parentId !== null) f.parentId = renamedIds.get(f.parentId) ?? f.parentId;
          f.id = renamedIds.get(f.id) ?? f.id;
        }
        for (const message of store.messages) {
          if (message.accountId !== accountId) continue;
          message.folderId = renamedIds.get(message.folderId) ?? message.folderId;
        }
        return { ...folder };
      },
      async remove(accountId, folderId, recursive) {
        await delay();
        const folder = requireFolder(store, accountId, folderId);
        mockRoleGuard(store, accountId, folder, 'deleted');
        const children = mockDescendants(store, accountId, folder);
        if (recursive !== true) {
          // MOCK, and load-bearing: the whole delete UI exists to put these
          // two numbers in front of a person before anything is destroyed.
          if (children.length > 0) {
            throw mockError(409, ApiErrorCodes.Conflict, 'This folder has folders inside it.', {
              reason: 'has_children',
              child_count: children.length,
            });
          }
          const messageCount = store.messages.filter((m) => m.folderId === folder.id).length;
          if (messageCount > 0) {
            throw mockError(409, ApiErrorCodes.Conflict, 'This folder still has mail in it.', {
              reason: 'not_empty',
              message_count: messageCount,
            });
          }
        }
        const doomed = new Set<string>([folder.id, ...children.map((f) => f.id)]);
        store.folders = store.folders.filter((f) => !doomed.has(f.id));
        store.messages = store.messages.filter((m) => !doomed.has(m.folderId));
        resync(store);
      },
      async focus(accountId, folderId) {
        // The mock has no server-side poll: new mail is whatever a test puts
        // in the store. The folder must still exist, as on the server.
        await delay();
        requireFolder(store, accountId, folderId);
      },
      async markAllRead(accountId, folderId) {
        await delay();
        requireFolder(store, accountId, folderId);
        let updatedCount = 0;
        for (const message of store.messages) {
          if (message.folderId === folderId && !message.flags.includes('seen')) {
            message.flags = [...message.flags, 'seen'];
            updatedCount += 1;
          }
        }
        resync(store);
        return { updatedCount };
      },
      async empty(accountId, folderId, confirmation) {
        await delay();
        const folder = requireFolder(store, accountId, folderId);
        // Mode by role, as the server decides it: Trash and Junk are emptied
        // for good, everything else goes to Trash.
        const mode = folder.role === 'trash' || folder.role === 'junk' ? 'permanent' : 'trash';
        const trash = store.folders.find((f) => f.accountId === accountId && f.role === 'trash');
        if (mode === 'trash' && trash === undefined) {
          throw mockError(404, ApiErrorCodes.NotFound, 'This mailbox has no Trash folder to empty into.', {
            reason: 'no_trash_folder',
          });
        }
        // Only this folder's own messages: subfolders are other mailboxes.
        const inFolder = store.messages.filter((m) => m.folderId === folder.id);
        // MOCK: the store has no UIDs, so "uid_next" is the count plus one —
        // enough for the round trip to be the real one's shape.
        const uidNext = inFolder.length + 1;
        if (
          confirmation === undefined ||
          confirmation.mode !== mode ||
          confirmation.uidValidity !== folder.uidValidity
        ) {
          if (inFolder.length === 0) return { emptiedCount: 0, mode };
          throw mockError(
            409,
            ApiErrorCodes.Conflict,
            mode === 'permanent'
              ? 'Deleting everything in this folder for good needs a confirmation.'
              : 'Moving everything in this folder to Trash needs a confirmation.',
            {
              reason: 'confirm_empty',
              mode,
              message_count: inFolder.length,
              uid_validity: folder.uidValidity,
              uid_next: uidNext,
            },
          );
        }
        const doomed = inFolder.slice(0, Math.max(0, confirmation.uidNext - 1));
        const ids = new Set(doomed.map((m) => m.id));
        if (mode === 'permanent') {
          store.messages = store.messages.filter((m) => !ids.has(m.id));
        } else {
          for (const message of doomed) message.folderId = trash!.id;
        }
        resync(store);
        return { emptiedCount: doomed.length, mode };
      },
      async roles(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        return mockFolderRoleChoices(store, accountId);
      },
      async setRole(accountId, role, folderId) {
        await delay();
        requireAccount(store, accountId);
        if (!CHOOSABLE_ROLES.includes(role)) {
          throw mockError(400, ApiErrorCodes.BadRequest, 'That role cannot be chosen.', {
            reason: 'unknown_role',
          });
        }
        const folder = requireFolder(store, accountId, folderId);
        // Settle every folder's automatic role before the first choice moves one.
        applyMockFolderRoles(store, accountId);
        if (folder.path.toUpperCase() === 'INBOX') {
          throw mockError(400, ApiErrorCodes.BadRequest, 'The Inbox cannot hold another role.', {
            reason: 'inbox',
          });
        }
        if (!folder.selectable) {
          throw mockError(400, ApiErrorCodes.BadRequest, 'That folder cannot hold messages.', {
            reason: 'not_selectable',
          });
        }
        const choices = (store.folderRoleChoices[accountId] ??= {});
        const other = CHOOSABLE_ROLES.find((r) => r !== role && choices[r] === folderId);
        if (other !== undefined) {
          throw mockError(
            409,
            ApiErrorCodes.Conflict,
            `That folder is already your ${other[0]!.toUpperCase()}${other.slice(1)} folder. Choose another, or set ${other} back to Automatic first.`,
            { reason: 'folder_has_role', role: other },
          );
        }
        choices[role] = folderId;
        applyMockFolderRoles(store, accountId);
        return mockFolderRoleChoices(store, accountId);
      },
      async clearRole(accountId, role) {
        await delay();
        requireAccount(store, accountId);
        const choices = store.folderRoleChoices[accountId];
        if (choices !== undefined) delete choices[role];
        applyMockFolderRoles(store, accountId);
      },
    },

    /* ------------------------------------------------------------ messages */
    messages: {
      async list(query, signal) {
        await delay(signal);
        requireAccount(store, query.accountId);
        requireFolder(store, query.accountId, query.folderId);

        let rows = store.messages.filter(
          (m) => m.accountId === query.accountId && m.folderId === query.folderId,
        );
        if (query.search !== undefined && query.search.trim() !== '') {
          const { text, matches } = labels.labelSearch(query.accountId, query.search);
          rows = rows.filter((m) => matches(m) && matchesText(m, text));
        }
        if (query.unreadOnly === true) rows = rows.filter((m) => !m.flags.includes('seen'));
        if (query.flaggedOnly === true) rows = rows.filter((m) => m.flags.includes('flagged'));

        const sorted = sortMessages(rows, query.sort);
        const page = paginate(sorted, query);
        return {
          ...page,
          sort: query.sort ?? DEFAULT_MESSAGE_SORT,
          sortsAvailable: MOCK_SORTS_AVAILABLE,
          items: page.items.map((m) => ({ ...m, flags: [...m.flags] })),
        };
      },
      async get(accountId, messageId, options) {
        await delay(options?.signal);
        const summary = requireMessage(store, accountId, messageId);
        const rule = matchRemoteRule(store, accountId, summary.from.email);
        // With no explicit mode the server decides from the persisted rules,
        // and blocks when none match. The client never defaults this.
        const mode: RemoteMode = options?.remote ?? (rule !== undefined ? 'load' : 'block');
        return withMockRemoteContent(buildMessageDetail(summary), mode, rule);
      },
      async raw(accountId, messageId, signal) {
        await delay(signal);
        return buildRawSource(buildMessageDetail(requireMessage(store, accountId, messageId)));
      },
      async authentication(accountId, messageId, signal): Promise<MessageAuthentication> {
        await delay(signal);
        return buildMessageAuthentication(requireMessage(store, accountId, messageId));
      },
      async rawBlob(accountId, messageId, signal) {
        await delay(signal);
        const source = buildRawSource(buildMessageDetail(requireMessage(store, accountId, messageId)));
        return new Blob([source], { type: 'message/rfc822' });
      },
      // MOCK: standalone mode has no user-content origin, so the session
      // advertises none and these answer exactly what a real backend without
      // one does (ADR 0078): 501.
      async render(_accountId, _messageId, options) {
        await delay(options?.signal);
        throw mockError(501, ApiErrorCodes.NotImplemented, 'the user-content origin is not implemented yet');
      },
      async renderStats(_accountId, _messageId, _renderId, signal) {
        await delay(signal);
        throw mockError(501, ApiErrorCodes.NotImplemented, 'the user-content origin is not implemented yet');
      },
      async partLink() {
        await delay();
        throw mockError(501, ApiErrorCodes.NotImplemented, 'the user-content origin is not implemented yet');
      },
      async attached(accountId, messageId, path, options) {
        await delay(options?.signal);
        const detail = buildMessageDetail(requireMessage(store, accountId, messageId));
        const first = path.split('-')[0];
        const outer = detail.attachments.find((a) => a.attachedPath === first);
        if (outer === undefined || path.split('-').length > 2) {
          throw mockError(404, ApiErrorCodes.NotFound, 'That attachment is not an email that can be opened.');
        }
        return buildAttachedMessage(accountId, messageId, path);
      },
      async renderAttached(_accountId, _messageId, _path, options) {
        await delay(options?.signal);
        throw mockError(501, ApiErrorCodes.NotImplemented, 'the user-content origin is not implemented yet');
      },
      async renderAttachedStats(_accountId, _messageId, _path, _renderId, signal) {
        await delay(signal);
        throw mockError(501, ApiErrorCodes.NotImplemented, 'the user-content origin is not implemented yet');
      },
      async attachedPartLink() {
        await delay();
        throw mockError(501, ApiErrorCodes.NotImplemented, 'the user-content origin is not implemented yet');
      },
      async updateFlags(accountId, messageIds, update: FlagUpdate) {
        await delay();
        requireAccount(store, accountId);
        const add = update.add ?? [];
        const remove = update.remove ?? [];
        const result = bulkApply(messageIds, (id) => {
          const message = requireMessage(store, accountId, id);
          const next = new Set<MessageFlag>(message.flags);
          for (const flag of add) next.add(flag);
          for (const flag of remove) next.delete(flag);
          message.flags = [...next];
          return undefined;
        });
        resync(store);
        return result;
      },
      async move(accountId, input) {
        await delay();
        const target = requireFolder(store, accountId, input.targetFolderId);
        if (!target.selectable) throw invalid(`"${target.path}" cannot hold messages.`);
        const result = bulkApply(input.messageIds, (id) => {
          const message = requireMessage(store, accountId, id);
          message.folderId = target.id;
          // No `newId`: this mock server does not advertise UIDPLUS, so the
          // destination UID is unknown and the id is unchanged. A server that
          // does will report one per applied item, and the client must take it
          // from the result rather than keeping the id it sent.
          return undefined;
        });
        resync(store);
        return result;
      },
      async copy(accountId, input) {
        await delay();
        const target = requireFolder(store, accountId, input.targetFolderId);
        if (!target.selectable) throw invalid(`"${target.path}" cannot hold messages.`);
        const result = bulkApply(input.messageIds, (id) => {
          const source = requireMessage(store, accountId, id);
          const copyId = newId('msg-copy');
          store.messages.push({
            ...source,
            id: copyId,
            folderId: target.id,
            flags: [...source.flags],
          });
          return copyId;
        });
        resync(store);
        return result;
      },
      /*
       * MOCK: ADR 0108's transfer between two of the owner's mailboxes, with
       * the server's refusals and per-message results. The duplicate guard is
       * a Message-ID search on the server; here each copy remembers which
       * source message it came from, which answers the same question.
       */
      async transfer(accountId, input) {
        await delay();
        requireAccount(store, accountId);
        requireAccount(store, input.targetAccountId);
        if (input.targetAccountId === accountId) {
          throw mockError(400, 'same_account', 'Use move or copy inside one mailbox.');
        }
        if (input.mode !== 'move' && input.mode !== 'copy') throw invalid('mode must be "move" or "copy".');
        if (input.messageIds.length === 0) throw invalid('At least one message id is required.');
        if (input.messageIds.length > MAX_TRANSFER_MESSAGES) {
          throw mockError(400, ApiErrorCodes.BadRequest, 'too many messages in one request', {
            field: 'message_ids',
            max: MAX_TRANSFER_MESSAGES,
          });
        }
        const target = requireFolder(store, input.targetAccountId, input.targetFolderId);
        if (!target.selectable) throw invalid(`"${target.path}" cannot hold messages.`);
        const results = input.messageIds.map((id): TransferItemResult => {
          const source = store.messages.find((m) => m.id === id && m.accountId === accountId);
          if (source === undefined) {
            return { id, status: 'failed', error: { code: ApiErrorCodes.NotFound, message: 'This message is no longer there.' } };
          }
          const origin = `${accountId}:${id}`;
          const existing = store.messages.find(
            (m) => m.accountId === target.accountId && m.folderId === target.id && transferOrigins.get(m) === origin,
          );
          let targetId = existing?.id;
          if (existing === undefined) {
            targetId = newId('msg-xfer');
            const copy: MessageSummary = {
              ...source,
              id: targetId,
              accountId: target.accountId,
              folderId: target.id,
              flags: [...source.flags],
            };
            transferOrigins.set(copy, origin);
            store.messages.push(copy);
          }
          if (input.mode === 'move') store.messages = store.messages.filter((m) => m !== source);
          const status = existing !== undefined ? 'already_there' : input.mode === 'move' ? 'moved' : 'copied';
          return targetId === undefined ? { id, status } : { id, status, targetId };
        });
        resync(store);
        return { results };
      },
      async archive(accountId, messageIds) {
        await delay();
        requireAccount(store, accountId);
        const result = bulkApply(messageIds, (id) => {
          const message = requireMessage(store, accountId, id);
          const archive = roleFolder(store, message.accountId, 'archive');
          message.folderId = archive.id;
          if (!message.flags.includes('seen')) message.flags = [...message.flags, 'seen'];
          return undefined;
        });
        resync(store);
        return result;
      },
      async remove(accountId, messageIds, expunge) {
        await delay();
        requireAccount(store, accountId);
        const doomed = new Set<string>();
        const result = bulkApply(messageIds, (id) => {
          const message = requireMessage(store, accountId, id);
          const trash = roleFolder(store, message.accountId, 'trash');
          if (expunge === true || message.folderId === trash.id) {
            // Already in Trash, or an explicit expunge: really gone.
            doomed.add(message.id);
          } else {
            message.folderId = trash.id;
            if (!message.flags.includes('deleted')) message.flags = [...message.flags, 'deleted'];
          }
          return undefined;
        });
        if (doomed.size > 0) {
          store.messages = store.messages.filter((m) => !doomed.has(m.id));
        }
        resync(store);
        return result;
      },
      async reportSpam(accountId, messageIds) {
        await delay();
        requireAccount(store, accountId);
        const junk = store.folders.find((f) => f.accountId === accountId && f.role === 'junk');
        // The whole request is refused, as the handler does: there is no
        // folder to move any of them into, and no folder is created for it.
        if (junk === undefined) {
          throw mockError(404, ApiErrorCodes.NotFound, 'This mailbox has no Junk folder.', {
            reason: 'no_junk_folder',
          });
        }
        const result = bulkApply(messageIds, (id) => {
          const message = requireMessage(store, accountId, id);
          message.folderId = junk.id;
          return undefined;
        });
        resync(store);
        return result;
      },
      async notSpam(accountId, messageIds) {
        await delay();
        requireAccount(store, accountId);
        const inbox = roleFolder(store, accountId, 'inbox');
        const result = bulkApply(messageIds, (id) => {
          const message = requireMessage(store, accountId, id);
          message.folderId = inbox.id;
          return undefined;
        });
        resync(store);
        return result;
      },
      async unsubscribe(accountId, messageId) {
        await delay();
        const message = requireMessage(store, accountId, messageId);
        const route = buildMessageDetail(message).unsubscribe;
        const junk = store.folders.find((f) => f.accountId === accountId && f.role === 'junk');
        if (junk !== undefined && message.folderId === junk.id) {
          throw mockError(
            409,
            ApiErrorCodes.Conflict,
            'Unsubscribe isn’t offered for mail in Junk.',
            { reason: 'in_junk' },
          );
        }
        if (route?.method !== 'one_click') {
          throw mockError(
            400,
            ApiErrorCodes.BadRequest,
            'This message does not offer one-click unsubscribe.',
            { reason: 'not_one_click' },
          );
        }
        // MOCK: nothing is posted anywhere; the host is the target's.
        return { status: 'done', host: new URL(route.target).host };
      },
      async search(accountId, input, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        const accountFolders = store.folders.filter(
          (f) => f.accountId === accountId && f.selectable,
        );
        const { text: withoutLabels, matches: labelled } = labels.labelSearch(accountId, input.query);
        const { text, inTerms } = splitInTerms(withoutLabels);

        // Which folders, decided the way the server decides it: `in:` terms
        // win over the scope, and the scope wins over the folder id.
        let searched: Folder[];
        let excluded: Folder[] = [];
        let scope: SearchPage['scope'];
        if (inTerms.length > 0) {
          scope = 'in';
          const anywhere = inTerms.some((term) => term.toLowerCase() === 'anywhere');
          if (anywhere) {
            searched = accountFolders;
          } else {
            const picked = new Map<string, Folder>();
            for (const term of inTerms) {
              const folder = resolveInFolder(accountFolders, term);
              if (folder === undefined) {
                throw mockError(400, ApiErrorCodes.BadRequest, `There is no folder called “${term}”.`, {
                  field: 'q',
                  folder: term,
                });
              }
              picked.set(folder.id, folder);
            }
            searched = [...picked.values()];
          }
        } else if (input.scope === 'all') {
          scope = 'all';
          const skip = (f: Folder) =>
            input.includeTrashJunk !== true && (f.role === 'trash' || f.role === 'junk');
          searched = accountFolders.filter((f) => !skip(f));
          excluded = accountFolders.filter(skip);
        } else {
          scope = 'folder';
          const inbox = accountFolders.find((f) => f.role === 'inbox');
          const id = input.folderId ?? inbox?.id;
          searched = id === undefined ? [] : [requireFolder(store, accountId, id)];
        }

        const ids = new Set(searched.map((f) => f.id));
        const rows = store.messages.filter(
          (m) => m.accountId === accountId && ids.has(m.folderId) && labelled(m) && matchesText(m, text),
        );
        const crossFolder = scope !== 'folder';
        const sort =
          input.sort ?? (crossFolder ? DEFAULT_CROSS_FOLDER_SORT : DEFAULT_MESSAGE_SORT);
        if (crossFolder && !CROSS_FOLDER_SORTS.includes(sort)) {
          throw mockError(400, ApiErrorCodes.BadRequest, 'That order cannot be used across folders.', {
            field: 'sort',
            supported: CROSS_FOLDER_SORTS,
          });
        }
        const page = paginate(sortMessages(rows, sort), input);
        return {
          ...page,
          sort,
          sortsAvailable: crossFolder
            ? MOCK_SORTS_AVAILABLE.filter((order) => CROSS_FOLDER_SORTS.includes(order))
            : MOCK_SORTS_AVAILABLE,
          scope,
          foldersSearched: searched.map((f) => f.id),
          ...(excluded.length > 0 ? { foldersExcluded: excluded.map((f) => f.id) } : {}),
          // `partial` is search-only: true when some folders were skipped.
          partial: false,
          items: page.items.map((m) => ({ ...m, flags: [...m.flags] })),
        };
      },
      async conversations(query, signal) {
        await delay(signal);
        requireAccount(store, query.accountId);
        const rows = mockConversations(store, query.accountId, query.folderId, query).map(
          (entry) => entry.conversation,
        );
        const sort = query.sort ?? 'date-desc';
        const ordered = sort === 'date-asc' ? [...rows].reverse() : rows;
        const page = paginate(ordered, query);
        return {
          ...page,
          sort,
          sortsAvailable: ['date-desc', 'date-asc'],
          state: rememberConversationState(query.accountId, query.folderId, rows),
          threading: 'local',
        };
      },
      async syncConversations(accountId, folderId, input, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        const before = conversationStates.get(input.state);
        const rows = mockConversations(store, accountId, folderId, input).map(
          (entry) => entry.conversation,
        );
        const state = rememberConversationState(accountId, folderId, rows);
        // A token this mock never minted (or minted for another folder) is
        // the server's "cannot be read": refetch in place.
        if (before === undefined || before.folder !== `${accountId}/${folderId}`) {
          return { state, upserts: [], removed: [], reset: true };
        }
        const now = new Set(rows.map((row) => row.id));
        const upserts = rows.filter((row) => before.rows.get(row.id) !== JSON.stringify(row));
        const removed = input.rows.map((row) => row.id).filter((id) => !now.has(id));
        if (upserts.length + removed.length > 100) {
          return { state, upserts: [], removed: [], reset: true };
        }
        return { state, upserts, removed, reset: false };
      },
      async conversation(accountId, messageId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        return mockConversationDetail(store, accountId, messageId);
      },
    },

    /* ------------------------------------------------------------- compose */
    compose: {
      async listDrafts(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        return store.drafts
          .filter((d) => d.accountId === accountId)
          .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
          .map((d) => ({ ...d }));
      },
      async getDraft(accountId, draftId, signal) {
        await delay(signal);
        return { ...requireDraft(store, accountId, draftId) };
      },
      async createDraft(accountId, input: DraftInput) {
        await delay();
        requireAccount(store, accountId);
        const { baseUpdatedAt: _base, ...fields } = input;
        const draft: Draft = {
          ...fields,
          // The account comes from the path, never from the body (doc 10 §6.4).
          accountId,
          id: newId('draft'),
          updatedAt: new Date().toISOString(),
          draftUuid: input.draftUuid ?? crypto.randomUUID(),
        };
        store.drafts.push(draft);
        return { ...draft };
      },
      async saveDraft(accountId, draftId, input: DraftInput) {
        await delay();
        requireAccount(store, accountId);
        const existing = store.drafts.find((d) => d.id === draftId && d.accountId === accountId);
        // Replaced or removed elsewhere: refused, nothing stored (ADR 0100).
        if (existing === undefined) throw draftBaseRefusal(store, accountId, draftId, input);
        const index = store.drafts.indexOf(existing);
        // A draft is a message in \Drafts: rewriting it appends a new one and
        // expunges the old, so the id changes on every save (doc 10 §4.2).
        const { baseUpdatedAt: _base, ...fields } = input;
        // The stored identity wins over the client's (ADR 0100).
        const draftUuid = existing.draftUuid ?? input.draftUuid ?? crypto.randomUUID();
        const next: Draft = {
          ...fields,
          accountId,
          id: newId('draft'),
          updatedAt: new Date().toISOString(),
          draftUuid,
        };
        retireDraft(store, { ...existing, draftUuid });
        store.drafts[index] = next;
        return { ...next };
      },
      async discardDraft(accountId, draftId) {
        await delay();
        retireDraft(store, requireDraft(store, accountId, draftId));
        store.drafts = store.drafts.filter((d) => d.id !== draftId);
      },
      async send(accountId, draftId) {
        await delay();
        const draft = requireDraft(store, accountId, draftId);
        retireDraft(store, draft);
        nextcloud.controls.requireUploads(draft.nextcloudUploadIds ?? []);
        const result = deliver(store, sentCopies, accountId, draft, newId('msg-sent'));
        store.drafts = store.drafts.filter((d) => d.id !== draftId);
        if (result.accepted.length > 0) {
          nextcloud.controls.markSent(draft.nextcloudUploadIds ?? [], accountId, draft.subject);
        }
        return result;
      },
      async sendDirect(accountId, { draftId, ...input }: SendInput) {
        await delay();
        requireAccount(store, accountId);
        // As the server does (ADR 0084 D13): an unknown upload id refuses the
        // send, and the rows become `sent` once a recipient was accepted.
        nextcloud.controls.requireUploads(input.nextcloudUploadIds ?? []);
        const result = deliver(store, sentCopies, accountId, input, newId('msg-sent'));
        if (result.accepted.length > 0) {
          nextcloud.controls.markSent(input.nextcloudUploadIds ?? [], accountId, input.subject);
        }
        // As the server does: the draft it was composed in goes once the
        // message is accepted, and stays when nobody took it.
        if (draftId !== undefined && result.accepted.length > 0) {
          const sent = store.drafts.find((d) => d.id === draftId && d.accountId === accountId);
          if (sent !== undefined) retireDraft(store, sent);
          store.drafts = store.drafts.filter((d) => d.id !== draftId);
        }
        return result;
      },
      async prepareReply(accountId, messageId, mode, signal) {
        await delay(signal);
        const account = requireAccount(store, accountId);
        const source = buildMessageDetail(requireMessage(store, accountId, messageId));
        const quoted = `<blockquote>${source.bodyText ?? ''}</blockquote>`;
        if (mode === 'forward') {
          // As the server does: nobody prefilled, no reply parent, the original
          // named for $Forwarded, and its files handed back as already staged.
          const attachments = source.attachments
            .filter((a) => !a.inline)
            .map((a) => ({
              id: newId('att'),
              filename: a.filename,
              contentType: a.contentType,
              sizeBytes: a.sizeBytes,
              inline: false,
            }));
          return {
            fromAddress: account.email,
            to: [],
            cc: [],
            bcc: [],
            subject: `Fwd: ${source.subject}`,
            bodyHtml: `<p><br></p><p>---------- Forwarded message ----------</p><div>${source.bodyText ?? ''}</div>`,
            bodyText: `\n\n---------- Forwarded message ----------\n\n${source.bodyText ?? ''}`,
            attachmentIds: attachments.map((a) => a.id),
            attachments,
            forwardedMessageId: source.id,
            mode,
          };
        }
        const to = [source.from];
        const cc = mode === 'reply-all' ? source.cc.map((a) => ({ ...a })) : [];
        return {
          fromAddress: account.email,
          to,
          cc,
          bcc: [],
          subject: `Re: ${source.subject}`,
          bodyHtml: quoted,
          bodyText: source.bodyText ?? '',
          attachmentIds: [],
          inReplyToMessageId: source.id,
          mode,
        };
      },
      async uploadAttachment(accountId, file, options) {
        await delay();
        requireAccount(store, accountId);
        // The real backend reports progress as the body goes out; the mock has
        // no body to send, so it reports the one value that is true.
        options?.onProgress?.(1);
        return {
          id: newId('att'),
          // The backend sanitizes the name and derives the type from the
          // bytes, so the mock answers with the browser's values rather than
          // pretending to do either.
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          inline: false,
        };
      },
      async discardAttachment(accountId) {
        await delay();
        requireAccount(store, accountId);
      },
    },

    /* ----------------------------------------------------- blocked senders */
    // MOCK: a block is a rule in the same Sieve state the Filters page edits,
    // exactly as the server keeps it, so blocking here shows up there.
    blockedSenders: {
      async list(accountId, signal) {
        await delay(signal);
        const state = blockableSieve(store, accountId);
        return {
          senders: (state.rules ?? [])
            .filter((rule) => rule.id.startsWith(BLOCKED_RULE_PREFIX))
            .map((rule) => ({ address: rule.conditions[0]?.value ?? '', ruleId: rule.id })),
        };
      },
      async block(accountId, input) {
        await delay();
        const address = input.address.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+$/.test(address)) {
          throw invalid(`“${input.address}” is not an email address.`);
        }
        const state = blockableSieve(store, accountId);
        const junk = store.folders.find((f) => f.accountId === accountId && f.role === 'junk');
        if (junk === undefined) {
          throw mockError(404, ApiErrorCodes.NotFound, 'This mailbox has no Junk folder.', {
            reason: 'no_junk_folder',
          });
        }
        const rules = state.rules ?? [];
        let rule = rules.find(
          (row) =>
            row.id.startsWith(BLOCKED_RULE_PREFIX) && row.conditions[0]?.value === address,
        );
        if (rule === undefined) {
          rule = {
            id: `${BLOCKED_RULE_PREFIX}${newId('rule')}`,
            name: `Blocked sender: ${address}`,
            enabled: true,
            match: 'all',
            conditions: [{ field: 'from', op: 'is', value: address }],
            actions: [{ type: 'fileinto', value: junk.path }, { type: 'stop' }],
            stopProcessing: true,
            order: rules.length + 1,
          };
          keepSieveBackup(store, accountId, 'block_sender', address);
          writeRules(store, accountId, state, [...rules, rule]);
        }
        const moved: BulkResult = { applied: [], failed: [] };
        if (input.moveExisting) {
          const inbox = store.folders.find((f) => f.accountId === accountId && f.role === 'inbox');
          const scope = new Set([inbox?.id, input.folderId].filter((id) => id !== undefined));
          for (const message of store.messages) {
            if (message.accountId !== accountId || !scope.has(message.folderId)) continue;
            if (message.from.email.toLowerCase() !== address) continue;
            message.folderId = junk.id;
            moved.applied.push({ id: message.id });
          }
          resync(store);
        }
        return { address, ruleId: rule.id, moved };
      },
      async unblock(accountId, address) {
        await delay();
        const state = blockableSieve(store, accountId);
        const wanted = address.trim().toLowerCase();
        const rules = state.rules ?? [];
        const next = rules.filter(
          (row) => !(row.id.startsWith(BLOCKED_RULE_PREFIX) && row.conditions[0]?.value === wanted),
        );
        if (next.length === rules.length) {
          throw mockError(404, ApiErrorCodes.NotFound, `${address} is not blocked.`, {
            reason: 'not_blocked',
          });
        }
        keepSieveBackup(store, accountId, 'unblock_sender', wanted);
        writeRules(store, accountId, state, next);
      },
    },

    /* --------------------------------------------------------------- sieve */
    sieve: {
      async get(accountId, signal) {
        await delay(signal);
        const account = requireAccount(store, accountId);
        if (!account.capabilities.sieve) {
          throw mockError(
            501,
            ApiErrorCodes.UnsupportedByHost,
            'This mailbox is on a host without managed filtering.',
            { feature: 'sieve' },
          );
        }
        return withProposed(
          cloneSieve(store.sieve[accountId] ?? { script: null, source: 'managed', rules: [] }),
        );
      },
      async put(accountId, input) {
        await delay();
        requireAccount(store, accountId);
        const current =
          store.sieve[accountId] ?? ({ script: null, source: 'managed', rules: [] } as SieveState);

        if ('rules' in input) {
          // The builder cannot own somebody else's script; overwriting it
          // would silently delete filtering the user still relies on (G6).
          if (current.source === 'foreign') {
            throw mockError(
              409,
              ApiErrorCodes.Conflict,
              'This mailbox has a Sieve script the rule builder cannot represent.',
              { reason: 'foreign_script' },
            );
          }
          // A script that was edited outside the builder needs the user to say
          // which version wins before the generated block replaces it.
          if (current.source === 'diverged' && input.allowOverwrite !== true) {
            throw mockError(
              409,
              ApiErrorCodes.Conflict,
              'This filter script was changed outside the rule builder.',
              {
                reason: 'diverged_script',
                current_script: current.script?.script ?? '',
                proposed_script: compileRules(input.rules),
              },
            );
          }
          const rules: SieveRule[] = input.rules.map((rule, index) => ({
            ...rule,
            order: index + 1,
          }));
          keepSieveBackup(store, accountId, 'filters');
          const next: SieveState = {
            source: 'managed',
            rules,
            vacation: current.vacation ?? null,
            script: {
              name: current.script?.name ?? 'ols-managed',
              active: true,
              script: compileRules(rules),
              updatedAt: new Date().toISOString(),
            },
          };
          store.sieve[accountId] = next;
          return cloneSieve(next);
        }

        // A raw script write: the structured model no longer describes what
        // runs, so the state is `diverged` until the builder is used again.
        keepSieveBackup(store, accountId, 'filters_script');
        const next: SieveState = {
          source: 'diverged',
          rules: current.rules,
          vacation: current.vacation ?? null,
          reason: 'The script was edited directly, so it may no longer match the rules below.',
          script: {
            name: current.script?.name ?? 'ols-managed',
            active: true,
            script: input.script,
            updatedAt: new Date().toISOString(),
          },
        };
        store.sieve[accountId] = next;
        return withProposed(cloneSieve(next));
      },
      async test(accountId, input) {
        await delay();
        requireAccount(store, accountId);

        // MOCK: a deliberately naive evaluator — enough to make the rule
        // builder's preview feel real without reimplementing Sieve. A raw
        // sample's headers are read too, or a pasted message could only ever
        // match on its body.
        const sample =
          input.sampleMessageId === undefined
            ? undefined
            : buildMessageDetail(requireMessage(store, accountId, input.sampleMessageId));
        const raw = input.sampleRaw ?? '';
        const rawHeader = (name: string) =>
          new RegExp(`^${name}:[ \\t]*(.*)$`, 'im').exec(raw.split(/\r?\n\r?\n/)[0] ?? '')?.[1] ?? '';
        const haystack: Record<string, string> = {
          from: sample === undefined ? rawHeader('from') : formatAddress(sample.from),
          to: sample === undefined ? rawHeader('to') : sample.to.map(formatAddress).join(', '),
          cc: sample === undefined ? rawHeader('cc') : sample.cc.map(formatAddress).join(', '),
          subject: sample?.subject ?? rawHeader('subject'),
          body: sample?.bodyText ?? raw,
          header: raw,
          size: String(sample?.sizeBytes ?? raw.length),
        };

        // A script (the raw editor's text) is read into the same shape; what
        // the mock cannot read is refused, never reported as "no match".
        const rules =
          'script' in input
            ? parseMockScript(input.script)
            : input.rules.filter((rule) => rule.enabled);
        if (rules === null) {
          throw invalid('The preview cannot run this script.');
        }

        const matchedRuleIds: string[] = [];
        const actions: SieveAction[] = [];
        evaluate: for (const rule of rules) {
          const results = rule.conditions.map((condition) => {
            const field = haystack[condition.field] ?? '';
            const value = condition.value.toLowerCase();
            const subject = field.toLowerCase();
            switch (condition.op) {
              case 'contains':
                return subject.includes(value);
              case 'not-contains':
                return !subject.includes(value);
              case 'is':
                return subject === value;
              case 'matches':
                return new RegExp(value.replace(/\*/g, '.*'), 'i').test(field);
              case 'greater-than':
                return Number.parseFloat(field) > Number.parseFloat(condition.value);
              case 'less-than':
                return Number.parseFloat(field) < Number.parseFloat(condition.value);
              default:
                return false;
            }
          });
          const matched =
            rule.match === 'all' ? results.every(Boolean) : results.some(Boolean);
          if (!matched) continue;
          matchedRuleIds.push(rule.id);
          for (const action of rule.actions) {
            actions.push({ ...action });
            if (action.type === 'stop') break evaluate;
          }
          if (rule.stopProcessing) break;
        }

        const result: SieveTestResult = {
          matchedRuleIds,
          actions,
          compiled: 'script' in input ? input.script : compileRules(input.rules),
          // The server's wording (sieve.actionNotes), close enough for a demo.
          notes: [
            ...new Set(
              actions.flatMap((action) => {
                switch (action.type) {
                  case 'vacation':
                    return [
                      `Would send an auto-reply, at most once per sender every ${
                        action.days !== undefined && action.days > 0
                          ? `${action.days} day${action.days === 1 ? '' : 's'}`
                          : "the host's default number of days"
                      }. Not simulated: nothing was sent, and whether this sender already had a reply cannot be known here.`,
                    ];
                  case 'redirect':
                    return [
                      `Would forward ${action.copy === true ? 'a copy of this message' : 'this message'} to ${action.value ?? ''}. Nothing was sent.`,
                    ];
                  case 'reject':
                    return ['Would bounce this message back to the sender. Nothing was sent.'];
                  default:
                    return [];
                }
              }),
            ),
          ],
        };
        return result;
      },
      async getVacation(accountId, signal) {
        await delay(signal);
        const account = requireAccount(store, accountId);
        if (!account.capabilities.sieve) {
          throw mockError(
            501,
            ApiErrorCodes.UnsupportedByHost,
            'This mailbox is on a host without managed filtering.',
            { feature: 'sieve' },
          );
        }
        return vacationStateOf(store, account);
      },
      async putVacation(accountId, input) {
        await delay();
        const account = requireAccount(store, accountId);
        if (!account.capabilities.sieve) {
          throw mockError(
            501,
            ApiErrorCodes.UnsupportedByHost,
            'This mailbox is on a host without managed filtering.',
            { feature: 'sieve' },
          );
        }
        const problem = vacationProblem(input.vacation);
        if (problem !== null) throw mockError(400, ApiErrorCodes.BadRequest, problem);
        const before = vacationStateOf(store, account);
        if (input.vacation.enabled && before.conflict !== undefined) {
          throw mockError(
            409,
            ApiErrorCodes.Conflict,
            `Only one automatic reply can run per message, and ${before.conflict} already sends one. Turn it off in Filters first.`,
            { reason: 'vacation_conflict' },
          );
        }
        const current =
          store.sieve[accountId] ?? ({ script: null, source: 'managed', rules: [] } as SieveState);
        keepSieveBackup(store, accountId, 'out_of_office');
        store.sieve[accountId] = { ...current, vacation: { ...input.vacation } };
        return vacationStateOf(store, account);
      },
      async listBackups(accountId, signal) {
        await delay(signal);
        // An account the store does not know (a test's own account list) has
        // no backups rather than a 404: the card then reads "none yet".
        const account = store.accounts.find((candidate) => candidate.id === accountId);
        if (account !== undefined && !account.capabilities.sieve) {
          throw mockError(
            501,
            ApiErrorCodes.UnsupportedByHost,
            'This mailbox is on a host without managed filtering.',
            { feature: 'sieve' },
          );
        }
        return {
          mailbox: account?.email ?? '',
          retention: { ...MOCK_SIEVE_RETENTION },
          backups: (store.sieveBackups[accountId] ?? []).map(({ script: _script, ...meta }) => meta),
        };
      },
      async getBackup(accountId, backupId, signal) {
        await delay(signal);
        const found = (store.sieveBackups[accountId] ?? []).find((b) => b.id === backupId);
        if (found === undefined) throw notFound('Backup', String(backupId));
        return { ...found };
      },
      async restoreBackup(accountId, backupId, input) {
        await delay();
        requireAccount(store, accountId);
        if (typeof input.baseHash !== 'string') {
          throw mockError(400, ApiErrorCodes.BadRequest, 'base_hash is required.');
        }
        const found = (store.sieveBackups[accountId] ?? []).find((b) => b.id === backupId);
        if (found === undefined) throw notFound('Backup', String(backupId));
        const current =
          store.sieve[accountId] ?? ({ script: null, source: 'managed', rules: [] } as SieveState);
        keepSieveBackup(store, accountId, 'restore', String(backupId));
        // What the builder can read back becomes rules again; anything else
        // is a raw script, as a raw save leaves it.
        const parsed = parseMockScript(found.script);
        const next: SieveState = {
          source: parsed === null ? 'diverged' : 'managed',
          rules: parsed ?? current.rules,
          vacation: current.vacation ?? null,
          ...(parsed === null
            ? { reason: 'An earlier version of the script was restored, so it may no longer match the rules below.' }
            : {}),
          script: {
            name: current.script?.name ?? 'ols-managed',
            active: true,
            script: found.script,
            updatedAt: new Date().toISOString(),
          },
        };
        store.sieve[accountId] = next;
        return cloneSieve(next);
      },
    },

    /* ------------------------------------------------------------ contacts */
    contacts: {
      async listBooks(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        return store.addressBooks
          .filter((b) => b.accountId === accountId)
          .map((b): AddressBook => ({
            ...b,
            contactCount: store.contacts.filter((c) => c.addressBookId === b.id).length,
          }));
      },
      async createBook(accountId, input: AddressBookInput) {
        await delay();
        requireAccount(store, accountId);
        const name = input.name.trim();
        if (name === '') throw invalid('An address book needs a name.');
        const book: AddressBook = {
          id: newId('book'),
          accountId,
          name,
          readOnly: false,
          href: `/dav/addressbooks/${newId('href')}/`,
          contactCount: 0,
          ...(input.description !== undefined ? { description: input.description } : {}),
        };
        store.addressBooks.push(book);
        return { ...book };
      },
      async updateBook(accountId, bookId, input: AddressBookPatch) {
        await delay();
        const book = requireBook(store, accountId, bookId);
        if (book.readOnly) throw invalid(`"${book.name}" is read-only.`);
        // Sparse: only the keys that were sent are written, so a rename never
        // clears a description the dialog did not show.
        if (input.name !== undefined) {
          const name = input.name.trim();
          if (name === '') throw invalid('An address book needs a name.');
          book.name = name;
        }
        if (input.description !== undefined) book.description = input.description;
        return { ...book };
      },
      async removeBook(accountId, bookId, confirm) {
        await delay();
        const book = requireBook(store, accountId, bookId);
        if (book.readOnly) throw invalid(`"${book.name}" is read-only.`);
        const count = store.contacts.filter((c) => c.addressBookId === book.id).length;
        // Like the handler: without `confirm` it is always a question, an
        // empty book included — only a confirmed call destroys anything.
        if (confirm !== count) {
          throw confirmDeleteRefusal('address_book', book.name, count, confirm !== undefined);
        }
        store.addressBooks = store.addressBooks.filter((b) => b.id !== book.id);
        store.contacts = store.contacts.filter((c) => c.addressBookId !== book.id);
      },
      async list(accountId, input, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        if (
          !store.addressBooks.some(
            (b) => b.id === input.addressBookId && b.accountId === accountId,
          )
        ) {
          throw notFound('Address book', input.addressBookId);
        }
        const needle = (input.search ?? '').trim().toLowerCase();
        // Group cards as the server treats them (#123): never rows; the first
        // page carries them as groups; `group` keeps that group's members.
        // MOCK: a member is `urn:uuid:<contact id>`, standing in for the UID.
        const inBook = store.contacts.filter((c) => c.addressBookId === input.addressBookId);
        const memberIds = (g: Contact) =>
          new Set((g.members ?? []).map((m) => m.replace(/^urn:uuid:/i, '').trim().toLowerCase()));
        const people = new Set(inBook.filter((c) => !c.isGroup).map((c) => c.id.toLowerCase()));
        const groupCards = inBook.filter((c) => c.isGroup);
        const groups: ContactGroup[] = groupCards
          .map((g) => {
            const ids = memberIds(g);
            return {
              id: g.id,
              name: g.displayName.trim() || 'Unnamed group',
              memberCount: ids.size,
              resolvedCount: [...ids].filter((m) => people.has(m)).length,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        const selected = input.group === undefined ? undefined : groupCards.find((g) => g.id === input.group);
        const selectedIds = selected ? memberIds(selected) : undefined;
        const rows = inBook
          .filter((c) => !c.isGroup)
          .filter((c) => input.group === undefined || (selectedIds?.has(c.id.toLowerCase()) ?? false))
          .filter(
            (c) =>
              needle === '' ||
              c.displayName.toLowerCase().includes(needle) ||
              (c.organization ?? '').toLowerCase().includes(needle) ||
              c.emails.some((e) => e.value.toLowerCase().includes(needle)),
          )
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        const page = paginate(rows, input);
        return {
          ...page,
          items: page.items.map((c) => ({ ...c })),
          total: rows.length,
          ...(input.cursor === undefined && groups.length > 0 ? { groups } : {}),
        };
      },
      async get(accountId, contactId, signal) {
        await delay(signal);
        return { ...requireContact(store, accountId, contactId) };
      },
      async create(accountId, addressBookId, input: ContactInput) {
        await delay();
        requireAccount(store, accountId);
        const book = store.addressBooks.find(
          (b) => b.id === addressBookId && b.accountId === accountId,
        );
        if (book === undefined) throw notFound('Address book', addressBookId);
        if (book.readOnly) throw invalid(`"${book.name}" is read-only.`);
        refuseUnknownContactKeys(input);
        const { photoBase64, photoRemove: _photoRemove, ...fields } = input;
        const id = newId('contact');
        const contact: Contact = {
          ...fields,
          addressBookId,
          id,
          ...(photoBase64 !== undefined ? { photoUrl: photoUrlFor(accountId, id) } : {}),
          etag: `"etag-${newId('c')}"`,
          updatedAt: new Date().toISOString(),
        };
        if (photoBase64 !== undefined) {
          store.contactPhotos[id] = { type: 'image/jpeg', base64: photoBase64 };
        }
        store.contacts.push(contact);
        if (book.contactCount !== undefined) book.contactCount += 1;
        return { ...contact };
      },
      async update(accountId, contactId, input: ContactInput, etag) {
        await delay();
        const existing = requireContact(store, accountId, contactId);
        // `If-Match` is the CardDAV write guard: a stale ETag means somebody
        // else changed the card since it was read, and the loser must be told.
        if (etag !== undefined && existing.etag !== undefined && etag !== existing.etag) {
          // `theirs` as the backend sends it (writeContactConflict): the card
          // it now holds, snake_case like every error detail, so the editor's
          // field-level panel has the same input here as in production (#120).
          throw mockError(409, ApiErrorCodes.Conflict, 'This contact changed since you opened it.', {
            current_etag: existing.etag,
            theirs: toWire(existing),
          });
        }
        refuseUnknownContactKeys(input);
        const index = store.contacts.indexOf(existing);
        const { photoBase64, photoRemove, ...fields } = input;
        if (photoRemove === true) delete store.contactPhotos[existing.id];
        else if (photoBase64 !== undefined) {
          store.contactPhotos[existing.id] = { type: 'image/jpeg', base64: photoBase64 };
        }
        const photoUrl =
          store.contactPhotos[existing.id] !== undefined
            ? (existing.photoUrl ?? photoUrlFor(accountId, existing.id))
            : undefined;
        // The backend's absent-means-keep rule for the four fields outside the
        // documented ContactInput (contacts.go): a key that is not there
        // leaves the stored value alone; an empty one clears it.
        const kept: Partial<Contact> = {};
        for (const key of ['nickname', 'urls', 'birthday', 'anniversary'] as const) {
          if (!(key in input) && existing[key] !== undefined) {
            Object.assign(kept, { [key]: existing[key] });
          }
        }
        const next: Contact = {
          ...kept,
          ...fields,
          id: existing.id,
          ...(photoUrl !== undefined ? { photoUrl } : {}),
          ...(existing.isGroup !== undefined ? { isGroup: existing.isGroup } : {}),
          ...(existing.members !== undefined ? { members: existing.members } : {}),
          etag: `"etag-${newId('c')}"`,
          updatedAt: new Date().toISOString(),
        };
        store.contacts[index] = next;
        return { ...next };
      },
      async remove(accountId, contactId) {
        await delay();
        const contact = requireContact(store, accountId, contactId);
        store.contacts = store.contacts.filter((c) => c.id !== contactId);
        delete store.contactPhotos[contactId];
        const book = store.addressBooks.find((b) => b.id === contact.addressBookId);
        if (book?.contactCount !== undefined) book.contactCount = Math.max(book.contactCount - 1, 0);
      },
      async photo(accountId, contactId, signal) {
        await delay(signal);
        requireContact(store, accountId, contactId);
        const photo = store.contactPhotos[contactId];
        if (photo === undefined) throw notFound('Photo', contactId);
        const bytes = Uint8Array.from(atob(photo.base64), (char) => char.charCodeAt(0));
        return new Blob([bytes], { type: photo.type });
      },
      async exportVcf(accountId, addressBookId, contactId) {
        await delay();
        requireAccount(store, accountId);
        if (!store.addressBooks.some((b) => b.id === addressBookId && b.accountId === accountId)) {
          throw notFound('Address book', addressBookId);
        }
        const cards = store.contacts
          .filter((c) => c.addressBookId === addressBookId)
          .filter((c) => contactId === undefined || c.id === contactId);
        if (contactId !== undefined && cards.length === 0) throw notFound('Contact', contactId);
        return new Blob([toVCardFile(cards)], { type: 'text/vcard;charset=utf-8' });
      },
      async importVcf(accountId, addressBookId, file, options) {
        await delay(options?.signal);
        requireAccount(store, accountId);
        const book = store.addressBooks.find(
          (b) => b.id === addressBookId && b.accountId === accountId,
        );
        if (book === undefined) throw notFound('Address book', addressBookId);
        if (book.readOnly) {
          throw mockError(403, ApiErrorCodes.Forbidden, 'that address book is read-only');
        }
        if (file.size > MOCK_IMPORT_MAX_BYTES) {
          throw mockError(
            413,
            ApiErrorCodes.MessageTooLarge,
            'that file is larger than the 5 MB an import may be; export the address book in parts',
            { field: 'file', reason: 'file_too_large', max_bytes: MOCK_IMPORT_MAX_BYTES },
          );
        }
        options?.onProgress?.(1);
        const text = await file.text();
        if (text.trim() === '') {
          throw mockError(400, ApiErrorCodes.BadRequest, 'that file is empty', {
            field: 'file',
            reason: 'empty_file',
          });
        }
        const cards = parseMockVcardFile(text, () => `urn:uuid:${newId('uid')}`);
        if (cards.length === 0) {
          throw mockError(400, ApiErrorCodes.BadRequest, 'that file has no vCards in it', {
            field: 'file',
            reason: 'not_vcard',
          });
        }
        if (cards.length > MOCK_IMPORT_MAX_CARDS) {
          throw mockError(
            400,
            ApiErrorCodes.BadRequest,
            `the file has ${cards.length} cards; at most ${MOCK_IMPORT_MAX_CARDS} can be imported at once`,
            { field: 'file', reason: 'too_many_cards', max_cards: MOCK_IMPORT_MAX_CARDS, found: cards.length },
          );
        }

        const inBook = new Set(
          store.contacts
            .filter((c) => c.addressBookId === addressBookId)
            .map((c) => store.contactUids[c.id])
            .filter((uid): uid is string => uid !== undefined),
        );
        const inFile = new Set<string>();
        const report: ContactImportReport = { total: cards.length, imported: 0, skipped: 0, failed: 0, results: [] };
        for (const card of cards) {
          const base = {
            index: card.index,
            displayName: card.displayName,
            uid: card.uid,
            ...(card.uidGenerated ? { uidGenerated: true } : {}),
          };
          let result: ContactImportResult;
          if (card.error) {
            result = { ...base, status: 'failed', ...card.error };
          } else if (inFile.has(card.uid)) {
            result = {
              ...base,
              status: 'skipped',
              reason: 'duplicate_in_file',
              message: 'an earlier card in this file has the same UID',
            };
          } else if (inBook.has(card.uid)) {
            result = { ...base, status: 'skipped', reason: 'duplicate', message: 'already in this address book' };
          } else {
            const id = newId('contact');
            store.contacts.push({
              id,
              addressBookId,
              displayName: card.displayName,
              ...(card.firstName ? { firstName: card.firstName } : {}),
              ...(card.lastName ? { lastName: card.lastName } : {}),
              ...(card.organization ? { organization: card.organization } : {}),
              emails: card.emails.map((value, i) => ({ type: 'other', value, ...(i === 0 ? { primary: true } : {}) })),
              phones: card.phones.map((value) => ({ type: 'other', value })),
              addresses: [],
              categories: [],
              etag: `"etag-${newId('c')}"`,
              updatedAt: new Date().toISOString(),
            });
            store.contactUids[id] = card.uid;
            if (book.contactCount !== undefined) book.contactCount += 1;
            result = { ...base, status: 'imported', contactId: id };
          }
          if (!card.error) inFile.add(card.uid);
          report.results.push(result);
          if (result.status === 'imported') report.imported += 1;
          else if (result.status === 'skipped') report.skipped += 1;
          else report.failed += 1;
        }
        return report;
      },
      async autocomplete(accountId, query, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        const bookIds = new Set(
          store.addressBooks.filter((b) => b.accountId === accountId).map((b) => b.id),
        );
        const needle = query.trim().toLowerCase();
        if (needle === '') return [];
        return store.contacts
          .filter((c) => bookIds.has(c.addressBookId))
          .filter(
            (c) =>
              c.displayName.toLowerCase().includes(needle) ||
              c.emails.some((e) => e.value.toLowerCase().includes(needle)),
          )
          .slice(0, 10)
          .map((c) => ({ ...c }));
      },
    },

    /* ------------------------------------------------------------ calendar */
    calendar: {
      async listCalendars(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        return store.calendars.filter((c) => c.accountId === accountId).map((c) => ({ ...c }));
      },
      async createCalendar(accountId, input: CalendarInput) {
        await delay();
        requireAccount(store, accountId);
        const name = input.name.trim();
        if (name === '') throw invalid('A calendar needs a name.');
        const calendar: Calendar = {
          id: newId('cal'),
          accountId,
          name,
          color: input.color ?? nextPaletteColor(store, accountId),
          readOnly: false,
          href: `/dav/calendars/${newId('href')}/`,
          visible: true,
          ...(input.description !== undefined ? { description: input.description } : {}),
        };
        store.calendars.push(calendar);
        return { ...calendar };
      },
      async updateCalendar(accountId, calendarId, input: CalendarPatch) {
        await delay();
        const calendar = requireCalendar(store, accountId, calendarId);
        // A *subscription* is read-only in its events and editable in its
        // collection: the feed belongs to the publisher, the row belongs to us.
        if (calendar.readOnly && calendar.subscribed !== true) {
          throw invalid(`"${calendar.name}" is read-only.`);
        }
        if (input.name !== undefined) {
          const name = input.name.trim();
          if (name === '') throw invalid('A calendar needs a name.');
          calendar.name = name;
        }
        if (input.color !== undefined) calendar.color = input.color;
        if (input.description !== undefined) calendar.description = input.description;
        return { ...calendar };
      },
      async removeCalendar(accountId, calendarId, confirm) {
        await delay();
        const calendar = requireCalendar(store, accountId, calendarId);
        if (calendar.readOnly && calendar.subscribed !== true) {
          throw invalid(`"${calendar.name}" is read-only.`);
        }
        // A subscription's events are the feed's, so it is asked about with a
        // count of zero; everything else with its own. Without `confirm` it
        // is always a question, an empty calendar included (as the handler).
        const count =
          calendar.subscribed === true
            ? 0
            : store.events.filter((e) => e.calendarId === calendar.id).length;
        if (confirm !== count) {
          throw confirmDeleteRefusal('calendar', calendar.name, count, confirm !== undefined);
        }
        store.calendars = store.calendars.filter((c) => c.id !== calendar.id);
        store.events = store.events.filter((e) => e.calendarId !== calendar.id);
      },
      async subscribeCalendar(accountId, input: CalendarSubscriptionInput) {
        await delay();
        requireAccount(store, accountId);
        const name = input.name.trim();
        if (name === '') throw invalid('A subscription needs a name.');
        checkFeedUrl(input.url);
        const calendar: Calendar = {
          id: newId('cal'),
          accountId,
          name,
          color: input.color ?? nextPaletteColor(store, accountId),
          readOnly: true,
          href: `/dav/subscriptions/${newId('href')}/`,
          visible: true,
          subscribed: true,
          source: input.url,
          // The feed was just proved reachable, so it has a last-good time and
          // no error. `sync_error` needs no mapping on either side: the
          // transport converts every key mechanically (ADR 0009) and this mock
          // sits above it, speaking `types.ts` directly.
          lastSyncedAt: new Date().toISOString(),
          // A host with "stale" in it subscribes and then reports a failed
          // refresh over a copy it still holds — the one state that is
          // otherwise unreachable in mocks mode, because nothing here runs a
          // background refresh. Both fields set at once, which is the point.
          ...(new URL(input.url).hostname.includes('stale')
            ? { syncError: 'We could not reach the feed at the last four attempts.' }
            : {}),
        };
        store.calendars.push(calendar);
        return { ...calendar };
      },
      async listEvents(query, signal) {
        await delay(signal);
        requireAccount(store, query.accountId);
        const allowed = new Set(
          store.calendars
            .filter((c) => c.accountId === query.accountId)
            .filter((c) => query.calendarIds === undefined || query.calendarIds.includes(c.id))
            .map((c) => c.id),
        );
        const rangeStart = Date.parse(query.start);
        const rangeEnd = Date.parse(query.end);
        if (Number.isNaN(rangeStart) || Number.isNaN(rangeEnd)) {
          throw invalid('start and end must be ISO-8601 timestamps.');
        }
        return store.events
          .filter((event) => allowed.has(event.calendarId))
          .filter((event) => {
            const start = Date.parse(event.start);
            const end = Date.parse(event.end);
            // Recurring series are returned whole; the UI expands the RRULE.
            if (event.recurrenceRule !== undefined) return start <= rangeEnd;
            return end >= rangeStart && start <= rangeEnd;
          })
          .map((event) => ({ ...event, attendees: event.attendees.map((a) => ({ ...a })) }))
          .sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
      },
      async getEvent(accountId, eventId, signal) {
        await delay(signal);
        return { ...requireEvent(store, accountId, eventId) };
      },
      async createEvent(accountId, calendarId, input: CalendarEventInput) {
        await delay();
        const calendar = requireCalendar(store, accountId, calendarId);
        if (calendar.readOnly) throw invalid(`"${calendar.name}" is read-only.`);
        // `editScope` is a write instruction, not a property of the event.
        const { editScope: _editScope, videoCallUrl, ...rest } = input;
        const fields = withMockVideoCall(rest, undefined, videoCallUrl);
        const id = newId('evt');
        const event: CalendarEvent = {
          ...fields,
          calendarId,
          id,
          uid: `${id}@dao-mail.com`,
          etag: `"evt-etag-${id}"`,
        };
        store.events.push(event);
        return { ...event };
      },
      async updateEvent(accountId, eventId, input: CalendarEventInput, etag) {
        await delay();
        const existing = requireEvent(store, accountId, eventId);
        if (etag !== undefined && existing.etag !== undefined && etag !== existing.etag) {
          throw mockError(409, ApiErrorCodes.Conflict, 'This event changed since you opened it.', {
            current_etag: existing.etag,
          });
        }
        const index = store.events.indexOf(existing);
        const { editScope: _editScope, videoCallUrl, ...rest } = input;
        const fields = withMockVideoCall(rest, existing.videoCallUrl, videoCallUrl);
        const next: CalendarEvent = {
          ...fields,
          id: existing.id,
          uid: existing.uid,
          etag: `"evt-etag-${newId('v')}"`,
        };
        store.events[index] = next;
        return { ...next };
      },
      async removeEvent(accountId, eventId) {
        await delay();
        const event = requireEvent(store, accountId, eventId);
        store.events = store.events.filter((e) => e !== event);
      },
      async respond(accountId, eventId, status) {
        await delay();
        const event = requireEvent(store, accountId, eventId);
        const me = sessionEmail().toLowerCase();
        let found = false;
        event.attendees = event.attendees.map((attendee): Attendee => {
          if (attendee.email.toLowerCase() !== me) return attendee;
          found = true;
          return { ...attendee, status };
        });
        if (!found) {
          event.attendees = [
            ...event.attendees,
            // No display name exists for the session owner (ADR 0010); the
            // address is the identity, and the UI renders it directly.
            { email: sessionEmail(), status },
          ];
        }
      },
      /**
       * MOCK: reply to an invitation that arrived as mail.
       *
       * Deliberately re-reads the ICS from the *message* — here, the invite
       * the fixtures attached to it — rather than trusting anything the caller
       * sent, which is the property the real endpoint exists to have. The
       * event is upserted into the named calendar by UID, so replying twice
       * moves one row instead of growing two.
       */
      async replyToInvite(accountId, calendarId, input) {
        await delay();
        const calendar = requireCalendar(store, accountId, calendarId);
        if (calendar.readOnly) throw invalid(`"${calendar.name}" is read-only.`);
        const summary = requireMessage(store, accountId, input.messageId);
        const invite = buildMessageDetail(summary).invite;
        if (invite === undefined || invite.partId !== input.partId) {
          throw notFound('Invitation part', input.partId);
        }
        if (invite.method !== 'REQUEST') {
          throw invalid('Only an invitation request can be replied to.');
        }
        // As the server: an unverified invitation is answered only when the
        // reader confirmed it (respondToMailInvite, `confirm_unverified`).
        if (!invite.trusted && input.confirmUnverified !== true) {
          throw invalid(
            'this invitation could not be verified as coming from its organizer; confirm before answering it',
          );
        }

        const me = sessionEmail();
        const attendees = withSelfStatus(invite.event.attendees, me, input.status);
        const existing = store.events.find(
          (candidate) => candidate.uid === invite.uid && candidate.calendarId === calendarId,
        );
        if (existing !== undefined) {
          existing.attendees = attendees;
          existing.etag = `"evt-etag-${newId('v')}"`;
          return { ...existing };
        }
        const id = newId('evt');
        const event: CalendarEvent = {
          ...invite.event,
          id,
          calendarId,
          attendees,
          etag: `"evt-etag-${id}"`,
        };
        store.events.push(event);
        return { ...event };
      },
      async exportIcs(accountId, calendarId, eventId) {
        await delay();
        const calendar = requireCalendar(store, accountId, calendarId);
        const events =
          eventId === undefined
            ? store.events.filter((e) => e.calendarId === calendarId)
            : store.events.filter((e) => e.calendarId === calendarId && e.id === eventId);
        if (eventId !== undefined && events.length === 0) throw notFound('Event', eventId);
        const lines = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//OLS Mail//Mock Fixtures//EN',
          `X-WR-CALNAME:${calendar.name}`,
          ...events.flatMap(eventToIcs),
          'END:VCALENDAR',
          '',
        ];
        return new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
      },
      async listShareLinks(accountId, calendarId, signal) {
        await delay(signal);
        requireCalendar(store, accountId, calendarId);
        // Oldest first, as the endpoint documents: the order a person created
        // them in is the order they remember them in, and a list that
        // reshuffles on every fetch makes "revoke the second one" a hazard.
        return {
          links: store.calendarShareLinks
            .filter((link) => link.calendarId === calendarId)
            .map(({ calendarId: _calendarId, ...link }) => ({ ...link })),
          maxLinks: MOCK_MAX_SHARE_LINKS,
        };
      },
      async createShareLink(accountId, calendarId, input) {
        await delay();
        const calendar = requireCalendar(store, accountId, calendarId);
        const label = input.label.trim();
        // The server's own refusals, not the form's. A mock that accepted
        // anything the client sent would agree with the client instead of with
        // the server, and could never show a disagreement between them.
        if (label === '') throw invalid('A share link needs a label.');
        if (label.length > MOCK_MAX_SHARE_LINK_LABEL) {
          throw invalid(`A label can be at most ${MOCK_MAX_SHARE_LINK_LABEL} characters.`);
        }
        if (input.privacy !== 'full' && input.privacy !== 'busy-only') {
          throw invalid('privacy must be full or busy-only');
        }
        if (input.expiresAt !== undefined) {
          const expires = Date.parse(input.expiresAt);
          // The server's window: in the future, at most a year (plus a day's
          // slack for the browser's clock) away.
          if (!(expires > Date.now())) throw invalid('expires_at must be in the future');
          if (expires > Date.now() + MOCK_MAX_SHARE_LINK_LIFETIME_MS) {
            throw invalid('a share link can expire at most a year from now');
          }
        }
        const existing = store.calendarShareLinks.filter((l) => l.calendarId === calendarId);
        // The server's cap counts live links only (#134).
        const live = existing.filter(
          (l) => l.expiresAt === undefined || Date.parse(l.expiresAt) > Date.now(),
        );
        if (live.length >= MOCK_MAX_SHARE_LINKS) {
          throw conflict(
            `This calendar already has ${MOCK_MAX_SHARE_LINKS} share links. Revoke one first.`,
          );
        }
        const link: MockCalendarShareLink = {
          id: newId('shr'),
          calendarId,
          label,
          privacy: input.privacy,
          createdAt: new Date().toISOString(),
          ...(input.expiresAt !== undefined
            ? { expiresAt: new Date(input.expiresAt).toISOString() }
            : {}),
        };
        store.calendarShareLinks.push(link);
        calendar.feedPublished = true;
        calendar.shareLinkCount = existing.length + 1;
        // The URL is built HERE and stored NOWHERE: the token never reaches
        // `store`, so no later call in this session can produce it again. That
        // is the shape of the real thing, and it is what makes "shown once"
        // testable rather than a promise in a doc comment.
        const url =
          `https://mail01.cloud-hosting.io/calendar/${calendar.id}/feed.ics` +
          `?token=${newId('token')}`;
        const { calendarId: _calendarId, ...wire } = link;
        return { ...wire, url };
      },
      async revokeShareLink(accountId, calendarId, linkId) {
        await delay();
        const calendar = requireCalendar(store, accountId, calendarId);
        // Idempotent: revoking a link that is already gone is the same
        // outcome the caller asked for, so it is not an error. Only the named
        // link goes — every other grant on this calendar keeps working, which
        // is the entire reason links are separate.
        const index = store.calendarShareLinks.findIndex(
          (link) => link.id === linkId && link.calendarId === calendarId,
        );
        if (index >= 0) store.calendarShareLinks.splice(index, 1);
        const remaining = store.calendarShareLinks.filter((l) => l.calendarId === calendarId);
        if (remaining.length === 0) {
          delete calendar.feedPublished;
          delete calendar.shareLinkCount;
        } else {
          calendar.shareLinkCount = remaining.length;
        }
      },
    },

    /* ---------------------------------------------------------------- push */
    push: {
      async publicKey(signal) {
        await delay(signal);
        return { publicKey: MOCK_PUSH_PUBLIC_KEY };
      },
      async subscribe(input) {
        await delay();
        const existing = store.pushSubscriptions.find((s) => s.endpoint === input.endpoint);
        if (existing !== undefined) {
          existing.lastUsedAt = new Date().toISOString();
          return { ...existing };
        }
        const record: PushSubscriptionRecord = {
          id: newId('push-sub'),
          endpoint: input.endpoint,
          ...(input.deviceLabel !== undefined ? { deviceLabel: input.deviceLabel } : {}),
          createdAt: new Date().toISOString(),
          lastUsedAt: new Date().toISOString(),
        };
        store.pushSubscriptions.push(record);
        return { ...record };
      },
      async unsubscribe(subscriptionId) {
        await delay();
        if (!store.pushSubscriptions.some((s) => s.id === subscriptionId)) {
          throw notFound('Push subscription', subscriptionId);
        }
        store.pushSubscriptions = store.pushSubscriptions.filter((s) => s.id !== subscriptionId);
      },
      async listSubscriptions(signal) {
        await delay(signal);
        return store.pushSubscriptions.map((s) => ({ ...s }));
      },
      async getBackgroundPush(signal) {
        await delay(signal);
        // Like the server: task reminders need no stored password, so the
        // scheduler's flag is on whatever the enrolment says.
        return { ...store.backgroundPush, taskReminders: true };
      },
      async enableBackgroundPush(input) {
        await delay();
        if (input.consentVersion !== BACKGROUND_PUSH_CONSENT_VERSION) {
          throw invalid('consent_version must be the version of the explanation that was shown');
        }
        if (store.pushSubscriptions.length === 0) {
          throw mockError(409, ApiErrorCodes.Conflict, 'Turn on notifications on this device first.', {
            reason: 'no_devices',
          });
        }
        if (input.password === '') {
          throw invalidCredentials('your mailbox password is required');
        }
        store.backgroundPush = {
          status: 'on',
          reason: '',
          consentVersion: BACKGROUND_PUSH_CONSENT_VERSION,
          enabledAt: new Date().toISOString(),
          calendarReminders: true,
        };
        return { ...store.backgroundPush };
      },
      async disableBackgroundPush() {
        await delay();
        store.backgroundPush = { status: 'off', reason: '', consentVersion: BACKGROUND_PUSH_CONSENT_VERSION };
      },
      async listBackgroundAccounts(signal) {
        await delay(signal);
        // Like the server: every connected mailbox, the ones that are off too.
        return store.accounts
          .filter((account) => !account.isPrimary)
          .map(
            (account) =>
              store.backgroundAccounts.find((entry) => entry.accountId === account.id) ?? {
                accountId: account.id,
                status: 'off' as const,
                reason: '' as const,
                consentVersion: BACKGROUND_ACCOUNT_CONSENT_VERSION,
              },
          )
          .map((entry) => ({ ...entry }));
      },
      async enableBackgroundAccount(accountId, input) {
        await delay();
        if (input.consentVersion !== BACKGROUND_ACCOUNT_CONSENT_VERSION) {
          throw invalid('consent_version must be the version of the explanation that was shown');
        }
        const account = requireAccount(store, accountId);
        // The primary has its own endpoint; this one is for connected mailboxes.
        if (account.isPrimary) throw notFound('Connected mailbox', accountId);
        if (store.pushSubscriptions.length === 0) {
          throw mockError(409, ApiErrorCodes.Conflict, 'Turn on notifications on this device first.', {
            reason: 'no_devices',
          });
        }
        if (input.password === '') {
          throw invalidCredentials('your sign-in password is required');
        }
        if (account.status === 'reauth-required') {
          throw mockError(
            409,
            ApiErrorCodes.StoredCredentialInvalid,
            `The password for ${account.email} no longer works. Reconnect it in Settings → Accounts first.`,
            { account_status: 'reauth-required' },
          );
        }
        const entry: BackgroundAccountStatus = {
          accountId,
          status: 'on',
          reason: '',
          consentVersion: BACKGROUND_ACCOUNT_CONSENT_VERSION,
          enabledAt: new Date().toISOString(),
        };
        store.backgroundAccounts = [
          ...store.backgroundAccounts.filter((other) => other.accountId !== accountId),
          entry,
        ];
        return { ...entry };
      },
      async disableBackgroundAccount(accountId) {
        await delay();
        requireAccount(store, accountId);
        store.backgroundAccounts = store.backgroundAccounts.filter(
          (entry) => entry.accountId !== accountId,
        );
      },
      async getPreferences(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        const prefs = store.notificationPreferences.find((p) => p.accountId === accountId);
        const owner = runningOwnerSnooze(store);
        // Like the server: no stored row answers the documented defaults, not
        // a 404, and a snooze that has ended is not reported.
        if (prefs === undefined) {
          return {
            accountId,
            enabled: true,
            folderIds: [],
            calendarReminders: true,
            taskReminders: true,
            pushPayloadDetail: 'minimal',
            // MOCK: the mock deployment allows full payloads, so the control
            // can be exercised; a real server reports its admin lock.
            fullPayloadAllowed: true,
            ...owner,
          };
        }
        const { snoozedUntil, ownerSnoozedUntil: _stale, ...rest } = prefs;
        const running = snoozedUntil !== undefined && Date.parse(snoozedUntil) > Date.now();
        return {
          ...rest,
          folderIds: [...prefs.folderIds],
          fullPayloadAllowed: true,
          ...(running ? { snoozedUntil } : {}),
          ...owner,
        };
      },
      async setPreferences(accountId, prefs: NotificationPreferences) {
        await delay();
        requireAccount(store, accountId);
        // The server's refusals that the page can actually trigger. Echoing
        // whatever it was given is how a mock hides a contract break.
        if (prefs.pushPayloadDetail !== 'minimal' && prefs.pushPayloadDetail !== 'full') {
          throw invalid('push_payload_detail must be minimal or full');
        }
        if (prefs.quietHours !== undefined && !prefs.quietHours.tz) {
          throw invalid(
            'quiet_hours.tz is required: the IANA time zone the times are in, e.g. Europe/Berlin',
          );
        }
        // Like the server: the Inbox plus five more folders are watched, and a
        // longer list is refused rather than silently trimmed.
        const inboxId = store.folders.find((f) => f.accountId === accountId && f.role === 'inbox')?.id;
        const besidesInbox = new Set(prefs.folderIds.filter((id) => id !== inboxId)).size;
        if (besidesInbox > 5) {
          throw invalid(
            `Choose at most 5 folders besides the Inbox (you chose ${besidesInbox}): new mail is checked in 6 folders, and the Inbox is always one of them`,
          );
        }
        const index = store.notificationPreferences.findIndex((p) => p.accountId === accountId);
        // Like the server, the server-reported fields are ignored.
        const { fullPayloadAllowed: _ignored, ownerSnoozedUntil: _owner, ...stored } = prefs;
        // Like the server: `task_reminders` is optional on PUT, so a client
        // cached from before task reminders (which never sends it) keeps the
        // stored value rather than turning them off. Absent everywhere is the
        // documented default, on.
        const sentTaskReminders = (prefs as Partial<NotificationPreferences>).taskReminders;
        const next: NotificationPreferences = {
          ...stored,
          accountId,
          taskReminders:
            sentTaskReminders ?? store.notificationPreferences[index]?.taskReminders ?? true,
        };
        if (index === -1) store.notificationPreferences.push(next);
        else store.notificationPreferences[index] = next;
        return {
          ...next,
          folderIds: [...next.folderIds],
          fullPayloadAllowed: true,
          ...runningOwnerSnooze(store),
        };
      },
      async setOwnerSnooze(until) {
        await delay();
        if (until !== null && Number.isNaN(Date.parse(until))) {
          throw invalid('until must be an RFC 3339 time or null');
        }
        store.ownerSnoozedUntil = until ?? undefined;
        return runningOwnerSnooze(store);
      },
      async sendTestPush() {
        await delay();
        // The mock has one sign-in, so every subscription here is "this
        // browser's". Nothing is actually pushed: there is no push service.
        if (store.pushSubscriptions.length === 0) {
          throw mockError(
            409,
            ApiErrorCodes.PushNotSubscribed,
            'This browser is not set up for notifications. Turn on notifications for this device above, then try again.',
          );
        }
        return { sent: store.pushSubscriptions.length, devices: store.pushSubscriptions.length };
      },
    },

    /* ------------------------------------------------------ remote content */
    remoteContent: {
      // Mock mode mints sealed refs no server answers, so this returns a real
      // 1x1 PNG rather than failing: the point of the standalone mode is that
      // the UI renders, and a broken image in every message would obscure the
      // states the mock exists to exercise.
      async subresource(path, signal) {
        await delay(signal);
        if (!path.startsWith('/') && !path.startsWith(API_BASE)) {
          throw new Error('subresource: refused a path that is not on this API');
        }
        const png = Uint8Array.from(
          atob('iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='),
          (ch) => ch.charCodeAt(0),
        );
        return new Blob([png], { type: 'image/png' });
      },
      async listRules(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        return store.remoteContentRules
          .filter((rule) => rule.accountId === accountId)
          .map(({ accountId: _owner, ...rule }) => ({ ...rule }));
      },
      async addRule(accountId, input: RemoteContentRuleInput) {
        await delay();
        requireAccount(store, accountId);
        const accountWide = input.kind === 'all' || input.kind === 'svg_all';
        const value = accountWide ? '' : input.value.trim().toLowerCase();
        // The messages are the *server's* to write; the mock speaks the same
        // 400s so the UI's "surface it verbatim" path is exercised locally.
        if ((input.kind === 'sender' || input.kind === 'svg_sender') && !value.includes('@')) {
          throw invalid('An address rule needs a full email address, like name@example.com.');
        }
        if (input.kind === 'domain' && value.includes('@')) {
          throw invalid('A domain rule cannot contain @ — use example.com or .example.com.');
        }
        if (!accountWide && value === '') throw invalid('A value is required.');
        if (
          store.remoteContentRules.some(
            (rule) =>
              rule.accountId === accountId && rule.kind === input.kind && rule.value === value,
          )
        ) {
          throw conflict('That rule already exists.');
        }
        const rule: MockRemoteRule = {
          id: newId('rcr'),
          accountId,
          kind: input.kind,
          value,
          createdAt: new Date().toISOString(),
        };
        store.remoteContentRules.push(rule);
        const { accountId: _owner, ...wire } = rule;
        return { ...wire };
      },
      async removeRule(accountId, ruleId) {
        await delay();
        requireAccount(store, accountId);
        const before = store.remoteContentRules.length;
        store.remoteContentRules = store.remoteContentRules.filter(
          (rule) => !(rule.accountId === accountId && rule.id === ruleId),
        );
        if (store.remoteContentRules.length === before) throw notFound('Remote content rule', ruleId);
      },
      async clearRules(accountId) {
        await delay();
        requireAccount(store, accountId);
        store.remoteContentRules = store.remoteContentRules.filter(
          (rule) => rule.accountId !== accountId,
        );
      },
      // MOCK: the stand-in subresource is always a PNG, so there is never an
      // SVG for this grant to unlock; it only has to answer like the server.
      async allowSvgForMessage(accountId) {
        await delay();
        requireAccount(store, accountId);
      },
      async allowSvgForAttached(accountId) {
        await delay();
        requireAccount(store, accountId);
      },
    },

    /* -------------------------------------------------- notification rules */
    notifications: {
      async listRules(accountId, signal) {
        await delay(signal);
        requireAccount(store, accountId);
        return store.notificationRules
          .filter((rule) => rule.accountId === accountId)
          .map((rule) => ({ ...rule }));
      },
      async addRule(accountId, input: NotificationRuleInput) {
        await delay();
        requireAccount(store, accountId);
        const value = input.value.trim().toLowerCase();
        if (value === '') throw invalid('A value is required.');
        if (input.kind === 'sender' && !value.includes('@')) {
          throw invalid('An address rule needs a full email address, like name@example.com.');
        }
        if (input.kind === 'domain' && value.includes('@')) {
          throw invalid('A domain rule cannot contain @ — use example.com or .example.com.');
        }
        if (
          store.notificationRules.some(
            (rule) =>
              rule.accountId === accountId && rule.kind === input.kind && rule.value === value,
          )
        ) {
          throw conflict(`There is already a rule for ${value}.`);
        }
        const rule: NotificationRule = {
          id: newId('nr'),
          accountId,
          kind: input.kind,
          value,
          action: input.action,
          createdAt: new Date().toISOString(),
        };
        store.notificationRules.push(rule);
        return { ...rule };
      },
      async removeRule(accountId, ruleId) {
        await delay();
        requireAccount(store, accountId);
        const before = store.notificationRules.length;
        store.notificationRules = store.notificationRules.filter(
          (rule) => !(rule.accountId === accountId && rule.id === ruleId),
        );
        if (store.notificationRules.length === before) throw notFound('Notification rule', ruleId);
      },
      async preview(accountId, limit, signal): Promise<NotificationPreview> {
        await delay(signal);
        requireAccount(store, accountId);
        const prefs = store.notificationPreferences.find((p) => p.accountId === accountId);
        const inbox = store.folders.find((f) => f.accountId === accountId && f.role === 'inbox');
        const recent = store.messages
          .filter((message) => message.accountId === accountId)
          .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
          .slice(0, Math.min(Math.max(limit ?? 50, 1), 200));
        return {
          enabled: prefs?.enabled ?? true,
          watchedFolders:
            prefs !== undefined && prefs.folderIds.length > 0
              ? [...prefs.folderIds]
              : inbox
                ? [inbox.id]
                : [],
          items: recent.map((message) => {
            const verdict = mockEvaluate(store, accountId, message);
            return {
              messageId: message.id,
              folderId: message.folderId,
              from: { ...message.from },
              subject: message.subject,
              date: message.date,
              decision: verdict.decision,
              reason: verdict.reason,
              ...(verdict.matchedRule ? { matchedRule: { ...verdict.matchedRule } } : {}),
            };
          }),
        };
      },
      async newMail(accountId, after, signal): Promise<NewMailPoll> {
        await delay(signal);
        requireAccount(store, accountId);
        const prefs = store.notificationPreferences.find((p) => p.accountId === accountId);
        const inbox = store.folders.find((f) => f.accountId === accountId && f.role === 'inbox');
        const watched =
          prefs !== undefined && prefs.folderIds.length > 0
            ? [...prefs.folderIds]
            : inbox
              ? [inbox.id]
              : [];
        const enabled = prefs?.enabled ?? true;
        // MOCK: the watermark is opaque to the client, so its shape is free.
        const watermark = `mock:${Date.now().toString(36)}`;
        if (!enabled) {
          return {
            watermark,
            events: [],
            suppressed: 0,
            enabled: false,
            watchedFolders: watched,
            pollAfterSeconds: 60,
            interim: true,
          };
        }
        // A first poll (no `after`) returns a watermark and nothing else, so
        // signing in never raises a backlog of toasts.
        return {
          watermark,
          events: [],
          suppressed: 0,
          enabled: true,
          watchedFolders: watched,
          pollAfterSeconds: after === undefined || after === '' ? 60 : 60,
          interim: true,
        };
      },
    },

    /* --------------------------------------------------------- preferences */
    preferences: {
      async get(signal) {
        await delay(signal);
        return { ...(store.userPreferences ?? DEFAULT_USER_PREFERENCES) };
      },
      async update(prefs) {
        await delay();
        // The two range checks the server makes that a form can actually
        // reach; the rest (tag shape, known zone) the form cannot produce.
        if (prefs.markReadDelaySeconds < 0 || prefs.markReadDelaySeconds > 60) {
          throw invalid('mark_read_delay_seconds must be between 0 and 60');
        }
        if (!(UNDO_SEND_CHOICES as readonly number[]).includes(prefs.undoSendSeconds)) {
          throw invalid('undo_send_seconds must be 5, 10, 20 or 30');
        }
        if ([...prefs.signature].length > 10000) {
          throw invalid('the signature is longer than 10000 characters');
        }
        // Both optional on the wire: omitted keeps what was saved.
        const saved = store.userPreferences ?? DEFAULT_USER_PREFERENCES;
        // As the server stores it: no trailing slash, and a written-out :443
        // (the only port there is, ADR 0070) dropped.
        const videoCallUrl = (prefs.videoCallUrl ?? saved.videoCallUrl)
          .trim()
          .replace(/\/+$/, '')
          .replace(/^(https:\/\/[^/?#]*?):0*443(?=\/|$)/i, '$1');
        const videoCallRoomPrefix = prefs.videoCallRoomPrefix ?? saved.videoCallRoomPrefix;
        // A pointer on the server too: a page cached before the field existed
        // must not switch conversations off by saving something else.
        const conversationView = prefs.conversationView ?? saved.conversationView;
        const urlProblem = videoServerProblem(videoCallUrl);
        if (urlProblem !== undefined) throw invalid(`video_call_url: ${urlProblem}`);
        const prefixProblem = roomPrefixProblem(videoCallRoomPrefix);
        if (prefixProblem !== undefined) throw invalid(`video_call_room_prefix: ${prefixProblem}`);
        const { updatedAt: _ignored, ...rest } = prefs;
        store.userPreferences = {
          ...rest,
          videoCallUrl,
          videoCallRoomPrefix,
          conversationView,
          updatedAt: new Date().toISOString(),
        };
        return { ...store.userPreferences };
      },
      async testVideoCall(url) {
        await delay();
        const problem = videoServerProblem(url);
        if (url.trim() === '') throw invalid('url: enter the address of a Jitsi server');
        if (problem !== undefined) throw invalid(`url: ${problem}`);
        const host = new URL(url.trim()).host;
        // MOCK: a host named "unreachable…" plays the one that does not answer.
        if (host.startsWith('unreachable')) {
          return { reachable: false, message: `${host} did not answer. Check the address.` };
        }
        return { reachable: true, message: `${host} answered. Video calls will be created there.` };
      },
    },

    /* -------------------------------------------------------- AI assistant */
    ai: createMockAiApi({
      delay,
      chunkDelayMs: baseLatency === 0 ? 0 : 30,
      // Summarize (ADR 0074): the "server" reads the message from the mailbox.
      readMessage: (accountId, messageId) => {
        const summary = requireMessage(store, accountId, messageId);
        const detail = buildMessageDetail(summary);
        const text = mockReadableText(detail);
        const folder = store.folders.find((f) => f.id === summary.folderId);
        // MOCK: the fixture bodies never quote, but a real reply does. The
        // earlier messages of the same thread stand in for the quote a reply
        // would carry, so "Summarize the whole thread" has something to do.
        // Only a reply quotes, and only the messages it replies to.
        const base = (s: string) => s.replace(/^((re|fwd?):\s*)+/i, '').trim().toLowerCase();
        const earlier = /^re:/i.test(summary.subject)
          ? store.messages
              .filter(
                (m) =>
                  m.accountId === accountId &&
                  m.id !== summary.id &&
                  base(m.subject) === base(summary.subject) &&
                  m.date < summary.date,
              )
              .sort((a, b) => b.date.localeCompare(a.date))
          : [];
        const quote = earlier
          .map((m) => {
            const body = buildMessageDetail(m).bodyText ?? '';
            return `On ${new Date(m.date).toUTCString()}, ${m.from.name ?? m.from.email} wrote:\n${body
              .trim()
              .split('\n')
              .map((line) => `> ${line}`)
              .join('\n')}`;
          })
          .join('\n\n');
        return {
          subject: detail.subject,
          from: detail.from.name ?? detail.from.email,
          text: quote === '' ? text : `${text.trim()}\n\n${quote}`,
          inJunk: folder?.role === 'junk',
          attachments: detail.attachments
            .filter((a) => !(a.inline && a.contentId !== undefined))
            .map((a) => ({ name: a.filename, type: a.contentType })),
        };
      },
      // Summarize the whole conversation (ADR 0077): the "server" finds the
      // members itself — Sent replies merged, oldest first — as the reading
      // pane's conversation detail does. Nothing comes from the client.
      readConversation: (accountId, messageId) => {
        const found = mockConversationDetail(store, accountId, messageId);
        return {
          subject: found.conversation.subject,
          omitted: found.omittedCount,
          messages: found.messages.map((m) => ({
            from: m.from.name ?? m.from.email,
            date: m.date,
            text: mockReadableText(buildMessageDetail(m)),
          })),
        };
      },
    }),

    /* --------------------------------------------------------------- tasks */
    tasks: createMockTasksApi({
      delay,
      newId,
      accountExists: (accountId) => store.accounts.some((a) => a.id === accountId),
      seed: mockTaskSeed(),
      // MOCK: what the server reads over IMAP. A mock message keeps its id when
      // it moves (no UIDPLUS), and its Message-ID is the fixtures' own.
      mail: {
        byId: (accountId, messageId) => mockMailRef(store, accountId, (m) => m.id === messageId),
        byRfcId: (accountId, rfcMessageId) =>
          mockMailRef(store, accountId, (m) => mockRfcMessageId(m) === rfcMessageId),
      },
    }),

    /* -------------------------------------------------- labels (plan/17) */
    labels: labels.api,

    /* ------------------------------------ send later and snooze (ADR 0076) */
    scheduling: createMockSchedulingApi({
      store,
      delay,
      newId,
      mockError,
      // MOCK: the stored background credential (ADR 0076 §1) is the enrolment
      // Settings → Notifications makes; the mock starts with none, so the
      // first schedule or snooze of a session answers 409 until it is made.
      enrolment: (account) =>
        account.isPrimary
          ? store.backgroundPush.status
          : (store.backgroundAccounts.find((entry) => entry.accountId === account.id)?.status ?? 'off'),
      deliver: (accountId, input, messageId) =>
        deliver(store, sentCopies, accountId, input, messageId),
      folderId: mockFolderId,
      resync: () => resync(store),
    }),

    /* ------------------------------------------------ nextcloud (plan 18) */
    nextcloud: nextcloud.api,

    /* ------------------------------------- copies of sent mail (#84) */
    sentCopies: sentCopies.api,
  };

  const api: MailApi = {
    // `login` and `session` must stay reachable while signed out — they are how
    // you stop being signed out. `logout` too: it is idempotent.
    auth: guardGroup(raw.auth, gate, new Set(['login', 'session', 'logout'])),
    events: guardGroup(raw.events, gate),
    accounts: guardGroup(raw.accounts, gate),
    folders: guardGroup(raw.folders, gate),
    messages: guardGroup(raw.messages, gate),
    compose: guardGroup(raw.compose, gate),
    sieve: guardGroup(raw.sieve, gate),
    blockedSenders: guardGroup(raw.blockedSenders, gate),
    contacts: guardGroup(raw.contacts, gate),
    calendar: guardGroup(raw.calendar, gate),
    push: guardGroup(raw.push, gate),
    remoteContent: guardGroup(raw.remoteContent, gate),
    notifications: guardGroup(raw.notifications, gate),
    preferences: guardGroup(raw.preferences, gate),
    ai: guardGroup(raw.ai, gate),
    tasks: guardGroup(raw.tasks, gate),
    scheduling: guardGroup(raw.scheduling, gate),
    labels: guardGroup(raw.labels, gate),
    nextcloud: guardGroup(raw.nextcloud, gate),
    sentCopies: guardGroup(raw.sentCopies, gate),
  };

  return {
    api,
    session: {
      expireSession: () => {
        signedIn = false;
      },
      setUpstreamAvailable: (available: boolean) => {
        upstreamAvailable = available;
      },
      isSignedIn: () => signedIn,
      setSentCopyFailure: (failure) => sentCopies.setFailure(failure),
      receiveMail: (partial) => {
        const id = partial.id ?? newId('msg-new');
        const message: MessageSummary = {
          threadId: `thr-${id}`,
          subject: 'New message',
          from: { name: 'Someone', email: 'someone@example.test' },
          to: [],
          date: new Date().toISOString(),
          snippet: '',
          flags: [],
          hasAttachments: false,
          sizeBytes: 2_048,
          labelKeywords: [],
          ...partial,
          id,
        };
        store.messages.push(message);
        resync(store);
        return message;
      },
    },
  };
}

/**
 * MOCK: one list for the primary mailbox, so the Tasks view has something in
 * it; the connected mailboxes start empty, which is how the empty state is
 * seen. Dates are relative to today so "Today" and "overdue" always show.
 */
function mockTaskSeed(): MockTaskSeed[] {
  const day = (offset: number): string => {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };
  return [
    {
      accountId: 'acct-primary',
      name: 'My tasks',
      color: 'blue',
      tasks: [
        { title: 'Reply to the venue about the deposit', dueDate: day(-1), starred: true },
        { title: 'Send the quarterly report', dueDate: day(0), dueTime: '15:00' },
        { title: 'Book the dentist', dueDate: day(1), notes: 'Ask for an early slot.' },
        { title: 'Renew the domain', dueDate: day(12) },
        { title: 'Water the plants', completed: true },
      ],
    },
  ];
}

/**
 * MOCK: the primary mailbox's labels. "Family & friends" takes the encoded
 * keyword form (§2) and "Someday" starts hidden from the sidebar, so both
 * paths are reachable without making anything first.
 */
const MOCK_LABEL_SEED: readonly MockLabelSeed[] = [
  { accountId: 'acct-primary', name: 'Work', color: 'blue' },
  { accountId: 'acct-primary', name: 'Travel', color: 'teal' },
  { accountId: 'acct-primary', name: 'Receipts', color: 'amber' },
  { accountId: 'acct-primary', name: 'Family & friends', color: 'pink' },
  { accountId: 'acct-primary', name: 'Someday', color: 'gray', showInSidebar: false },
];

/** MOCK: keywords on every few primary Inbox messages, deterministically. */
function seedLabelKeywords(messages: MessageSummary[]): void {
  const work = mockLabelKeyword('Work');
  const travel = mockLabelKeyword('Travel');
  const receipts = mockLabelKeyword('Receipts');
  const family = mockLabelKeyword('Family & friends');
  let index = 0;
  for (const message of messages) {
    if (message.accountId !== 'acct-primary' || !message.id.includes('-inbox-')) continue;
    const keywords: string[] = [];
    // One spelled in another case, as a server keeps the first STORE's spelling.
    if (index % 5 === 1) keywords.push(index === 6 ? work.toLowerCase() : work);
    if (index % 7 === 2) keywords.push(travel);
    if (index % 11 === 3) keywords.push(receipts);
    if (index % 13 === 4) keywords.push(family);
    message.labelKeywords = keywords;
    index += 1;
  }
}

/** The plain adapter, for every call site that does not drive the session. */
export function createMockApi(options: MockApiOptions = {}): MailApi {
  return createMockApiWithSession(options).api;
}

export type { MockCalendarShareLink, MockSeed };

/* MOCK: re-exported so devtools/tests can build a second independent store. */
export { createSeedData };
